/**
 * PROPRIETARY NOTICE
 * THE ONESCENT DIAL - Main Application Component
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * Powered by Cycles in Harmony™ (CiH) and Spacio-Temporal Analysis™
 * All rights reserved.
 */
import React, { useState, useEffect, lazy, Suspense, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';

// Configure axios defaults for CORS - credentials MUST be true for cross-origin auth
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';

import Dial from './components/Dial';
import QuickStatusOverlay from './components/QuickStatusOverlay';
import ContextTiles from './components/ContextTiles';
import SEOHead from './components/SEOHead';
import { useAuth } from './contexts/AuthContext';
import { useCyKL } from './contexts/CyKLContext';
import cosmicCache from './utils/cosmicCache';
import { AspectsPanelSkeleton, ComparisonPanelSkeleton } from './components/SkeletonLoaders';
import { Clock, MapPin, Calendar, Info, Layers, User, LogOut, Shield, HelpCircle, History, Save, Share2, Cake, StickyNote, Sparkles, Download, Trash2, KeyRound, Users, Star, BookOpen, Package, Settings, RefreshCw, Calculator, Key, X } from 'lucide-react';
import { OPERATIONAL_PREFERENCES, BRANDING_META, getButtonClasses, getMenuItemClasses } from './utils/styleCard';
import { getVersionString } from './utils/version';
import { useKeyboardNavigation, useReducedMotion } from './hooks/useKeyboardNavigation';
import { usePWAInstall } from './hooks/usePWAInstall';
import { useTheme } from './contexts/ThemeContext';
import { toast } from './components/ui/sonner';
import logger from './utils/logger';
import './App.css';

// Lazy load all modal/panel components for faster initial load
const AspectsPanel = lazy(() => import('./components/AspectsPanel'));
const ForecastPanel = lazy(() => import('./components/ForecastPanel'));
const UniversalInfoPanel = lazy(() => import('./components/UniversalInfoPanel'));
const PhaseInfoPanel = lazy(() => import('./components/PhaseInfoPanel'));
const ComparisonSynthesisPanel = lazy(() => import('./components/ComparisonSynthesisPanel'));
const ComparisonOverlay = lazy(() => import('./components/ComparisonOverlay'));
const AuthModal = lazy(() => import('./components/AuthModal'));
const SearchHistory = lazy(() => import('./components/SearchHistory'));
const ExportShare = lazy(() => import('./components/ExportShare'));
const PrivacyPolicyModal = lazy(() => import('./components/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicyModal })));
const OnboardingTutorial = lazy(() => import('./components/OnboardingTutorial'));
const DateTimePicker = lazy(() => import('./components/DateTimePicker'));
const ModulationDetailPanel = lazy(() => import('./components/ModulationDetailPanel'));
const IngressOverlay = lazy(() => import('./components/overlays/IngressOverlay'));
const LocationController = lazy(() => import('./components/LocationController'));
const ComparativeAnalysisSetup = lazy(() => import('./components/ComparativeAnalysisSetup'));
const ChangePasswordModal = lazy(() => import('./components/ChangePasswordModal'));
const ProfileManager = lazy(() => import('./components/ProfileManager'));
const GlossaryPanel = lazy(() => import('./components/GlossaryPanel'));
const MultiViewVisualization = lazy(() => import('./components/MultiViewVisualization'));
const TimeLapsePlayer = lazy(() => import('./components/TimeLapsePlayer'));
const KeyboardShortcutsModal = lazy(() => import('./components/KeyboardShortcutsModal'));
const DeepDivePanel = lazy(() => import('./components/DeepDivePanel'));
const CyKLChat = lazy(() => import('./components/CyKLChat'));
const CosmicTicker = lazy(() => import('./components/CosmicTicker'));
const HorizonSmoke = lazy(() => import('./components/horizon/HorizonSmoke'));
const HorizonInterface = lazy(() => import('./components/horizon/HorizonInterface'));
const BirthChartBanner = lazy(() => import('./components/BirthChartBanner'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const NotificationBanner = lazy(() => import('./components/NotificationBanner'));
const ExperienceSettings = lazy(() => import('./components/ExperienceSettings'));
const CosmicBackground = lazy(() => import('./components/CosmicBackground'));
const MyNotesPanel = lazy(() => import('./components/MyNotesPanel'));
const CompleteProfileModal = lazy(() => import('./components/CompleteProfileModal'));

// Experience controls
import { ExperienceQuickToggle, ExperienceSettingsPanel } from './components/ExperienceSettings';
import { useExperience } from './contexts/ExperienceContext';

// Loading fallback for lazy components
const LazyFallback = () => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
    <div className="text-[var(--tod-brass)] text-xl brass-glow animate-pulse">Loading...</div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function App() {

  const { user, isAuthenticated, logout, getProfiles, loading: authLoading } = useAuth();
  const { playSound, showSettings, initializeAudio, soundPrefs, audioInitialized, triggerHaptic } = useExperience();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [searchHistoryInitialTab, setSearchHistoryInitialTab] = useState('places'); // 'dials' | 'places' | 'profiles' | 'synthesis' | 'times'
  const [showExportShare, setShowExportShare] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showProfileManager, setShowProfileManager] = useState(false);
  const [showHorizonInterface, setShowHorizonInterface] = useState(false);
  const [profileManagerInitialTab, setProfileManagerInitialTab] = useState('profiles'); // 'profiles' | 'credits' | 'preferences'
  const [showGlossaryPanel, setShowGlossaryPanel] = useState(false);
  const [glossaryTargetTerm, setGlossaryTargetTerm] = useState(null); // Term to scroll to when glossary opens
  const [glossaryTargetFAQ, setGlossaryTargetFAQ] = useState(null); // FAQ to expand when glossary opens
  const [glossaryInitialTab, setGlossaryInitialTab] = useState('glossary'); // 'glossary' | 'faq'
  const [showQuickStatus, setShowQuickStatus] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showPhasePanel, setShowPhasePanel] = useState(null); // null or phase name (NOURISH, ACTIVATE, etc.)
  const [deepDivePanel, setDeepDivePanel] = useState({ isOpen: false, type: null, itemKey: null }); // For element/modality/archetype deep dives
  const [profileCount, setProfileCount] = useState(0); // Track saved profile count
  const [showAdminPanel, setShowAdminPanel] = useState(false); // Admin panel for product keys
  const [hasAdminAccess, setHasAdminAccess] = useState(false); // Track if user has admin/vii role
  
  // Invitation flow state
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  
  // Password reset flow state (from URL)
  const [authModalMode, setAuthModalMode] = useState('login');
  const [passwordResetToken, setPasswordResetToken] = useState('');
  
  // Theme and accessibility
  const { cycleTheme, themeId } = useTheme();
  const prefersReducedMotion = useReducedMotion();
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  
  const [cosmicData, setCosmicData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [apiError, setApiError] = useState(null);
  
  // Load profile count when authenticated (exclude natal profile - count additional profiles only)
  useEffect(() => {
    const loadProfileCount = async () => {
      if (isAuthenticated && getProfiles) {
        try {
          const profiles = await getProfiles();
          // Filter out the natal profile (is_natal = true) to only count additional profiles
          const additionalProfiles = profiles?.filter(p => !p.is_natal) || [];
          setProfileCount(additionalProfiles.length);
        } catch (err) {
          logger.error('Failed to load profile count:', err);
        }
      } else {
        setProfileCount(0);
      }
    };
    loadProfileCount();
  }, [isAuthenticated, getProfiles, user]);
  
  // Check if user has admin/vii access
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!isAuthenticated) {
        setHasAdminAccess(false);
        return;
      }
      try {
        const token = localStorage.getItem('tod_auth_token');
        const response = await fetch(`${API}/auth/admin/check-access`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const text = await response.text();
          const data = JSON.parse(text);
          setHasAdminAccess(data.has_admin_access || false);
        } else {
          setHasAdminAccess(false);
        }
      } catch (err) {
        setHasAdminAccess(false);
      }
    };
    checkAdminAccess();
  }, [isAuthenticated, user]);
  
  // Monitor online/offline status and process retry queue when online
  useEffect(() => {
    const handleOnline = async () => {
      setIsOffline(false);
      // Process any queued operations when back online
      const { default: storageService } = await import('./services/StorageService');
      storageService.processRetryQueue();
    };
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Auto-initialize audio on first user interaction (browser requirement)
  useEffect(() => {
    if (audioInitialized) return; // Already initialized
    
    const handleFirstInteraction = () => {
      initializeAudio();
      // Remove listeners after first interaction
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
    
    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('keydown', handleFirstInteraction, { once: true });
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });
    
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [audioInitialized, initializeAudio]);
  
  // Listen for openGlossary custom events from GlossaryTerm components
  useEffect(() => {
    const handleOpenGlossary = (event) => {
      const { term } = event.detail || {};
      setGlossaryTargetTerm(term || null);
      setGlossaryInitialTab('glossary'); // Ensure glossary tab is selected
      setShowGlossaryPanel(true);
    };
    
    window.addEventListener('openGlossary', handleOpenGlossary);
    return () => window.removeEventListener('openGlossary', handleOpenGlossary);
  }, []);
  
  // Listen for openFAQ custom events from GlossaryTerm components
  useEffect(() => {
    const handleOpenFAQ = (event) => {
      const { faq } = event.detail || {};
      setGlossaryTargetFAQ(faq || null);
      setGlossaryInitialTab('faq'); // Switch to FAQ tab
      setShowGlossaryPanel(true);
    };
    
    window.addEventListener('openFAQ', handleOpenFAQ);
    return () => window.removeEventListener('openFAQ', handleOpenFAQ);
  }, []);
  
  // Initialize location from localStorage or default
  const [location, setLocation] = useState(() => {
    try {
      const saved = localStorage.getItem('tod_global_location');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.latitude && parsed.longitude) {
          return parsed;
        }
      }
    } catch (e) {
      logger.log('Error reading saved location:', e);
    }
    // Return default Denver location initially - will be replaced by actual location
    // This prevents errors while waiting for geolocation
    return { 
      latitude: 39.7392364, 
      longitude: -104.984862, 
      city: 'Denver', 
      state: 'CO', 
      country: 'USA',
      isDefault: true  // Flag to indicate this should be replaced
    };
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  // Extended date info for BCE support (stores astronomical year, isBCE flag, and ISO date string)
  const [extendedDateInfo, setExtendedDateInfo] = useState(null);
  const [showAspectsPanel, setShowAspectsPanel] = useState(false);
  const [aspectsPanelInitialOverlay, setAspectsPanelInitialOverlay] = useState(null); // For ticker events to open specific overlays
  const [showForecastPanel, setShowForecastPanel] = useState(false);
  const [forecastType, setForecastType] = useState('weekly'); // 'weekly' or 'monthly'
  
  // Universal info panel state
  const [universalPanel, setUniversalPanel] = useState(null);
  const [interpretations, setInterpretations] = useState(null);
  const [navigationSource, setNavigationSource] = useState(null); // Track where navigation came from (e.g., 'quickStatus')
  
  // Modulation detail panel state (for retrogrades, aspects, stelliums)
  const [modulationPanel, setModulationPanel] = useState(null);

  // Ingress overlay state - opened from ticker, AspectsPanel, planet/sign info panels
  const [ingressOverlay, setIngressOverlay] = useState(null);

  // Comparative Analysis state
  const [showComparativeSetup, setShowComparativeSetup] = useState(false);
  const [comparativeMode, setComparativeMode] = useState(null);
  const [secondaryCosmicData, setSecondaryCosmicData] = useState(null);
  const [showComparisonPanel, setShowComparisonPanel] = useState(false);
  const [showDeepSynthesis, setShowDeepSynthesis] = useState(false);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  
  // Multi-view visualization states (Global View, Temporal View)
  const [multiViewConfig, setMultiViewConfig] = useState(null);
  
  // Time Lapse player state
  const [timeLapseConfig, setTimeLapseConfig] = useState(null);
  
  // Import toast for notifications
  const { toast } = require('./components/ui/sonner');

  // Computed display data for comparison mode
  // Chart 1: Use chart1Data if custom datetime exists, otherwise use live cosmicData
  const chart1DisplayData = comparativeMode?.chart1?.datetime 
    ? (comparativeMode.chart1Data || null)  // Explicit null if not yet loaded
    : cosmicData;
  
  // Chart 2: Use secondaryCosmicData if custom datetime exists, otherwise use live cosmicData  
  const chart2DisplayData = comparativeMode?.chart2?.datetime 
    ? (secondaryCosmicData || null)  // Explicit null if not yet loaded
    : cosmicData;
  
  // Check if comparison data is ready
  const comparisonDataReady = comparativeMode ? (
    (comparativeMode.chart1?.datetime ? !!comparativeMode.chart1Data : !!cosmicData) &&
    (comparativeMode.chart2?.datetime ? !!secondaryCosmicData : !!cosmicData)
  ) : false;

  // Zoom state - now managed by ExperienceContext
  // Import from context instead of local state
  const { 
    zoomLevel, 
    handleZoomIn: contextZoomIn, 
    handleZoomOut: contextZoomOut, 
    resetZoom: contextZoomReset,
    setZoom: setZoomLevel,
    MIN_ZOOM,
    MAX_ZOOM,
    canZoomIn,
    canZoomOut
  } = useExperience();

  // Get user location on mount - always try geolocation first for accuracy
  useEffect(() => {
    const savedLocation = localStorage.getItem('tod_global_location');
    
    // If we have a saved location that's not the default NYC, use it
    if (savedLocation) {
      try {
        const parsed = JSON.parse(savedLocation);
        // Only use saved location if it's not the default Denver coordinates
        const isDefaultDenver = parsed.latitude === 39.7392364 && parsed.longitude === -104.984862;
        if (parsed.latitude && parsed.longitude && !isDefaultDenver) {
          setLocation(parsed);
          return; // Use saved non-default location
        }
      } catch (e) {
        logger.log('Error parsing saved location:', e);
      }
    }
    
    // Try to get actual user location via geolocation API
    const getActualLocation = async () => {
      // First, try browser geolocation (most accurate)
      if (navigator.geolocation) {
        const geoOptions = {
          enableHighAccuracy: true,  // Request high accuracy
          timeout: 10000,  // 10 second timeout (increased for mobile)
          maximumAge: 60000  // Accept cached position up to 1 minute old
        };
        
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            let cityName = `${lat.toFixed(2)}°, ${lng.toFixed(2)}°`;
            let stateName = '';
            let countryName = '';
            
            // Try to reverse geocode to get actual city name
            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`,
                { headers: { 'User-Agent': 'TheOnescentDial/2.27' } }
              );
              if (response.ok) {
                const text = await response.text();
                const data = JSON.parse(text);
                cityName = data.address?.city || data.address?.town || data.address?.village || data.address?.county || cityName;
                stateName = data.address?.state || '';
                countryName = data.address?.country || '';
              }
            } catch (e) {
              logger.log('Reverse geocode failed, using coordinates:', e);
            }
            
            const newLocation = {
              latitude: lat,
              longitude: lng,
              city: cityName,
              state: stateName,
              country: countryName
            };
            setLocation(newLocation);
            localStorage.setItem('tod_global_location', JSON.stringify(newLocation));
            logger.log('Location set from browser geolocation:', cityName);
          },
          async (error) => {
            logger.log('Browser geolocation failed:', error.message, '- trying IP fallback');
            // Fallback to IP-based geolocation
            await tryIPGeolocation();
          },
          geoOptions
        );
      } else {
        // Browser doesn't support geolocation, try IP fallback
        await tryIPGeolocation();
      }
    };
    
    // IP-based geolocation fallback
    const tryIPGeolocation = async () => {
      try {
        // Try ipapi.co first (free, no key required)
        const response = await fetch('https://ipapi.co/json/', { 
          timeout: 5000,
          headers: { 'Accept': 'application/json' }
        });
        if (response.ok) {
          const text = await response.text();
          const data = JSON.parse(text);
          if (data.latitude && data.longitude) {
            const newLocation = {
              latitude: data.latitude,
              longitude: data.longitude,
              city: data.city || 'Unknown',
              state: data.region || '',
              country: data.country_name || ''
            };
            setLocation(newLocation);
            localStorage.setItem('tod_global_location', JSON.stringify(newLocation));
            logger.log('Location set from IP geolocation:', data.city);
            return;
          }
        }
      } catch (e) {
        logger.log('IP geolocation failed:', e);
      }
      
      // Final fallback - use a neutral default (but still try to detect)
      // Set location to null state to show "Set Location" prompt
      setLocation({
        latitude: 39.7392364,
        longitude: -104.984862,
        city: 'Denver',
        state: 'CO',
        country: 'USA',
        isDefault: true  // Flag to indicate this is a fallback
      });
      logger.log('Using default Denver location as final fallback');
    };
    
    getActualLocation();
  }, []);

  // Parse URL parameters for shared views
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Check for email verification token (?verify=xxx)
    const verifyToken = params.get('verify');
    if (verifyToken) {
      // Call verification API
      const API = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${API}/api/auth/verify-email/${verifyToken}`)
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            toast.success('Email Verified!', {
              description: data.message || 'Your email has been verified successfully.'
            });
          } else {
            toast.error('Verification Failed', {
              description: data.message || 'Invalid or expired verification link.'
            });
          }
        })
        .catch(() => {
          toast.error('Verification Error', {
            description: 'Could not verify email. Please try again.'
          });
        });
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    
    // Check for password reset token (?reset=xxx)
    const resetToken = params.get('reset');
    if (resetToken) {
      setPasswordResetToken(resetToken);
      setAuthModalMode('reset');
      setShowAuthModal(true);
      // Clear URL params after capturing token
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    
    // Check for invitation token (?invite=xxx)
    const invite = params.get('invite');
    if (invite) {
      setInviteToken(invite);
      setShowCompleteProfileModal(true);
      // Clear URL params after capturing token
      window.history.replaceState({}, '', window.location.pathname);
      return; // Don't process other params if invitation is present
    }
    
    // Check for shared view parameters
    const lat = params.get('lat');
    const lng = params.get('lng') || params.get('lon');
    const date = params.get('date');
    const city = params.get('city');
    const state = params.get('state');
    const country = params.get('country');
    const autoExport = params.get('autoExport');
    
    if (lat && lng) {
      // Load shared view with comprehensive location data
      const newLocation = {
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        city: city || 'Shared Location',
        state: state || '',
        country: country || ''
      };
      setLocation(newLocation);
      
      if (date) {
        // Handle both datetime-local format (YYYY-MM-DDTHH:MM) and date format (YYYY-MM-DD)
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          setSelectedDate(parsedDate);
        }
      }
      
      // Build description from available location components
      const locationParts = [city, state, country].filter(Boolean);
      const locationDesc = locationParts.length > 0 ? locationParts.join(', ') : 'View from shared link';
      
      toast.success('View loaded', {
        description: locationDesc
      });
      
      // Auto-open export modal if requested (from Content Studio)
      if (autoExport === 'true') {
        // Small delay to let the dial render first
        setTimeout(() => {
          setShowExportShare(true);
          toast.info('Export panel ready', { description: 'Generate your PNG or PDF' });
        }, 1500);
      }
      
      // Clear URL params after loading
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle location change - persist to localStorage
  const handleLocationChange = (newLocation) => {
    setLocation(newLocation);
    localStorage.setItem('tod_global_location', JSON.stringify(newLocation));
    setShowLocationPicker(false);
    toast.success('Location updated', {
      description: newLocation.city || 'Coordinates set'
    });
  };

  // Zoom handlers - delegated to ExperienceContext
  const handleZoomIn = contextZoomIn;
  const handleZoomOut = contextZoomOut;
  const handleZoomReset = contextZoomReset;

  // Handle pinch-to-zoom on touch devices
  const handlePinchZoom = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY * -0.01;
      const newZoom = Math.min(Math.max(zoomLevel + delta, MIN_ZOOM), MAX_ZOOM);
      setZoomLevel(newZoom);
    }
  };

  // Handle comparative analysis activation
  // Fetches all required data BEFORE setting comparative mode to avoid race conditions
  const handleActivateComparison = useCallback(async (config) => {
    logger.log('[Comparison] Activating with config:', config);
    
    // Guard: If base cosmic data isn't loaded and comparison needs it, fetch first
    const needsBaseData = !config.chart1?.datetime || !config.chart2?.datetime;
    let currentCosmicData = cosmicData;
    
    if (needsBaseData && !currentCosmicData && location?.latitude && location?.longitude) {
      logger.log('[Comparison] Base cosmic data needed but not loaded, fetching inline...');
      toast.info('Loading cosmic data first...', { duration: 2000 });
      
      // Fetch base cosmic data inline (same logic as fetchCosmicDataWithRetry)
      try {
        const dateToUse = selectedDate || new Date();
        const cachedData = cosmicCache.get(location.latitude, location.longitude, dateToUse);
        if (cachedData) {
          currentCosmicData = cachedData;
          setCosmicData(cachedData);
        } else {
          const authToken = localStorage.getItem('tod_auth_token');
          const response = await axios.post(`${API}/cosmic-positions`, {
            datetime: dateToUse.toISOString(),
            latitude: location.latitude,
            longitude: location.longitude,
            city: location.city || null
          }, {
            timeout: 10000,
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
            }
          });
          cosmicCache.set(location.latitude, location.longitude, dateToUse, response.data);
          currentCosmicData = response.data;
          setCosmicData(response.data);
        }
      } catch (err) {
        logger.error('[Comparison] Failed to fetch base cosmic data:', err);
      }
      
      // If still no data after fetch, abort
      if (!currentCosmicData) {
        toast.error('Unable to load cosmic data. Please try again.');
        return;
      }
    }
    
    setComparisonLoading(true);
    
    // Handle Global View - opens MultiViewVisualization
    if (config.isGlobalView) {
      // Use user-selected primary location from config
      const primaryLoc = config.primaryLocation || config.chart1?.location;
      
      // DEBUG: Log what we're setting
      console.log('[App.js] Setting multiViewConfig for Global View:', {
        configGlobalDatetime: config.globalDatetime,
        configLocations: config.globalLocations?.length,
        primaryLoc: primaryLoc?.city
      });
      
      setMultiViewConfig({
        mode: 'global',
        primaryLocation: {
          name: primaryLoc?.city || 'Your Location',
          lat: primaryLoc?.latitude || location.latitude,
          lon: primaryLoc?.longitude || location.longitude
        },
        locations: config.globalLocations || [],
        // CRITICAL: Pass the selected datetime for accurate phase calculation
        datetime: config.globalDatetime || null
      });
      setComparisonLoading(false);
      return;
    }
    
    // Handle Temporal View - opens MultiViewVisualization
    if (config.isTemporalView) {
      // Use user-selected location from temporalConfig
      const temporalLoc = config.temporalConfig?.location || config.chart1?.location;
      setMultiViewConfig({
        mode: 'temporal',
        primaryLocation: {
          name: temporalLoc?.city || 'Your Location',
          lat: temporalLoc?.latitude || location.latitude,
          lon: temporalLoc?.longitude || location.longitude
        },
        timePoints: config.temporalConfig?.timePoints || []
      });
      setComparisonLoading(false);
      return;
    }
    
    // Handle Time Lapse - opens TimeLapsePlayer
    if (config.isTimeLapse) {
      // Use user-selected location from timeLapseConfig
      const tlLoc = config.timeLapseConfig?.location;
      setTimeLapseConfig({
        startDate: config.timeLapseConfig?.startDate,
        endDate: config.timeLapseConfig?.endDate,
        steps: config.timeLapseConfig?.steps || 7,
        location: {
          name: tlLoc?.city || 'Your Location',
          lat: tlLoc?.latitude || location.latitude,
          lon: tlLoc?.longitude || location.longitude
        }
      });
      setComparisonLoading(false);
      return;
    }
    
    // Helper to fetch chart data with caching
    const fetchChartData = async (chartConfig) => {
      if (!chartConfig.datetime) {
        // "Current Sky" - return null to use live cosmicData
        return null;
      }
      
      const lat = chartConfig.location?.latitude || location.latitude;
      const lon = chartConfig.location?.longitude || location.longitude;
      const datetime = new Date(chartConfig.datetime);
      
      // Check cache first
      const cachedData = cosmicCache.get(lat, lon, datetime);
      if (cachedData) {
        logger.log('Cache hit for comparison chart data');
        return cachedData;
      }
      
      try {
        const response = await axios.post(`${API}/cosmic-positions`, {
          datetime: chartConfig.datetime,
          latitude: lat,
          longitude: lon
        });
        
        // Store in cache
        cosmicCache.set(lat, lon, datetime, response.data);
        return response.data;
      } catch (error) {
        logger.error('Error fetching chart data:', error);
        return null;
      }
    };
    
    // Fetch BOTH chart data in parallel before setting state
    const [chart1Data, chart2Data] = await Promise.all([
      fetchChartData(config.chart1),
      fetchChartData(config.chart2)
    ]);
    
    // Set secondary cosmic data (for Chart 2 when it has a datetime)
    setSecondaryCosmicData(chart2Data);
    
    // Set comparative mode with all data attached (single state update)
    const newComparativeMode = {
      ...config,
      chart1Data: chart1Data  // null if current sky, data object if custom/profile
    };
    logger.log('[Comparison] Setting comparativeMode:', {
      relationshipType: newComparativeMode.relationshipType?.label,
      chart1: newComparativeMode.chart1,
      chart2: newComparativeMode.chart2,
      hasChart1Data: !!newComparativeMode.chart1Data,
      hasChart2Data: !!chart2Data
    });
    setComparativeMode(newComparativeMode);
    
    setComparisonLoading(false);
    
    // Show info toast about the comparison type
    toast.info(`${config.relationshipType?.label || 'Comparative Analysis'} active`, {
      description: 'Tap Synthesis for detailed interpretation'
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cosmicData, location, selectedDate]);

  // Exit comparative mode
  const exitComparativeMode = () => {
    setComparativeMode(null);
    setSecondaryCosmicData(null);
    toast.success('Returned to single view');
  };

  // Fetch all interpretations on mount (cached for the session)
  useEffect(() => {
    const fetchInterpretations = async () => {
      try {
        const response = await axios.get(`${API}/interpretations/all`);
        setInterpretations(response.data);
      } catch (error) {
        logger.error('Error fetching interpretations:', error);
      }
    };
    fetchInterpretations();
  }, []);

  // Update display time every second (for clock display only)
  // Use separate state for display vs. data fetching to prevent full re-renders
  const [displayTime, setDisplayTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => {
      setDisplayTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  
  // Fetch cosmic data with caching and retry logic
  const fetchCosmicDataWithRetry = useCallback(async (retryCount = 0) => {
    // Skip if location is not yet available
    if (!location || !location.latitude || !location.longitude) {
      logger.log('Skipping cosmic data fetch - location not available yet');
      return null;
    }
    
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 2000;
    const REQUEST_TIMEOUT = 10000; // 10 second timeout for mobile
    const dateToUse = selectedDate || new Date();
    
    // For BCE dates, use the ISO date string directly; otherwise use the Date object
    const getDateTimeString = () => {
      if (extendedDateInfo?.isBCE && extendedDateInfo.isoDate) {
        // Format: "-0006-08-22T12:00:00" for BCE dates
        const time = extendedDateInfo.time || '12:00';
        return `${extendedDateInfo.isoDate}T${time}:00`;
      }
      return dateToUse.toISOString();
    };
    
    // Check cache first
    const cachedData = cosmicCache.get(location.latitude, location.longitude, dateToUse);
    if (cachedData) {
      logger.log('Cache hit - using cached cosmic data');
      setCosmicData(cachedData);
      setLoading(false);
      setApiError(null);
      return cachedData; // Return the cached data
    }
    
    try {
      setLoading(true);
      setApiError(null);
      // Get auth token for cross-origin requests
      const authToken = localStorage.getItem('tod_auth_token');
      
      // Use fetch instead of axios for better compatibility
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
      
      const response = await fetch(`${API}/cosmic-positions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          datetime: getDateTimeString(),
          latitude: location.latitude,
          longitude: location.longitude,
          city: location.city || null
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : {};
      
      // Store in cache
      cosmicCache.set(location.latitude, location.longitude, dateToUse, data);
      setCosmicData(data);
      setApiError(null);
      
      // Log cache stats in development
      if (process.env.NODE_ENV === 'development') {
        logger.log('Cache stats:', cosmicCache.getStats());
      }
      
      return data; // Return the fetched data
    } catch (error) {
      // Enhanced error logging for debugging deployment issues
      logger.error('Error fetching cosmic data:', {
        message: error?.message || String(error),
        name: error?.name,
        url: `${API}/cosmic-positions`
      });
      
      if (retryCount < MAX_RETRIES && !isOffline) {
        // Retry after delay
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchCosmicDataWithRetry(retryCount + 1);
      }
      
      setApiError(isOffline 
        ? 'You appear to be offline. Please check your connection.'
        : error?.code === 'ECONNABORTED' 
          ? 'Request timed out. Please check your connection and try again.'
          : 'Unable to load celestial data. Please try again.'
      );
      return null; // Return null on failure
    } finally {
      setLoading(false);
    }
  }, [location, selectedDate, extendedDateInfo, isOffline]);
  
  // Manual retry function for user-triggered retry
  const retryFetch = useCallback(() => {
    cosmicCache.clear(); // Clear cache on manual retry
    fetchCosmicDataWithRetry(0);
  }, [fetchCosmicDataWithRetry]);
  
  useEffect(() => {
    fetchCosmicDataWithRetry(0);
  }, [fetchCosmicDataWithRetry, selectedDate, extendedDateInfo, isOffline]);

  const handleDateSelect = (date, extendedInfo = null) => {
    setSelectedDate(date);
    setExtendedDateInfo(extendedInfo); // Store BCE metadata if provided
    setShowDatePicker(false);
    
    // Format the date for the toast
    let formattedDate;
    if (extendedInfo?.isBCE) {
      // For BCE dates, format manually using the extended info
      const parsed = extendedInfo.isoDate?.match(/^(-?\d+)-(\d{2})-(\d{2})/);
      if (parsed) {
        const month = parseInt(parsed[2], 10);
        const day = parseInt(parsed[3], 10);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const displayYear = Math.abs(extendedInfo.astronomicalYear) + 1; // Convert astronomical to display year
        formattedDate = `${monthNames[month - 1]} ${day}, ${displayYear} BCE`;
      } else {
        formattedDate = `Year ${Math.abs(extendedInfo.astronomicalYear) + 1} BCE`;
      }
    } else {
      formattedDate = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
    toast.success('Date selected', {
      description: formattedDate
    });
  };

  const resetToNow = () => {
    setSelectedDate(null);
    setExtendedDateInfo(null); // Clear BCE metadata
    toast.success('Viewing current sky', {
      description: 'Real-time celestial positions'
    });
  };

  // Restart tutorial handler
  const handleRestartTutorial = () => {
    // Reset tutorial completion in localStorage
    localStorage.removeItem('tod_onboarding_complete');
    setShowTutorial(true);
  };

  // Save current view to history (requires authentication)
  const saveToHistory = async (tier = 'session') => {
    try {
      // Ensure we have valid location data
      if (!location || (typeof location.latitude !== 'number' && typeof location.lat !== 'number')) {
        toast.error('Cannot save - location not set');
        return;
      }

      // Handle both lat/lng and latitude/longitude formats
      const lat = location.latitude ?? location.lat;
      const lng = location.longitude ?? location.lng;

      // Generate descriptive label based on what's being saved
      const generateLabel = () => {
        // Multi-View (Global/Temporal)
        if (multiViewConfig) {
          if (multiViewConfig.mode === 'global') {
            const locationNames = multiViewConfig.locations?.map(l => l.name || l.city).slice(0, 3).join(', ');
            return `Global View: ${multiViewConfig.primaryLocation?.name || 'Base'} → ${locationNames || 'Multiple Locations'}`;
          } else {
            const timeCount = multiViewConfig.timePoints?.length || 0;
            return `Temporal View: ${multiViewConfig.primaryLocation?.name || 'Location'} (${timeCount} moments)`;
          }
        }
        
        // Time Lapse
        if (timeLapseConfig) {
          const start = timeLapseConfig.startDate ? new Date(timeLapseConfig.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Start';
          const end = timeLapseConfig.endDate ? new Date(timeLapseConfig.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'End';
          return `Time Lapse: ${timeLapseConfig.location?.name || 'Location'} (${start} → ${end})`;
        }
        
        // Comparative Analysis
        if (comparativeMode) {
          const relType = comparativeMode.relationshipType?.label || 'Comparison';
          
          // Build descriptive chart labels from actual data
          const getChartDescription = (chart, defaultLabel) => {
            if (!chart) return defaultLabel;
            
            // If it's a profile, use the profile name
            if (chart.profileName || chart.name) {
              return chart.profileName || chart.name;
            }
            
            // If it's current sky
            if (chart.type === 'current') {
              return 'Current Sky';
            }
            
            // If it has a custom datetime, format it
            if (chart.datetime) {
              const dt = new Date(chart.datetime);
              const dateStr = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              const locStr = chart.location?.city || chart.location?.name || '';
              return locStr ? `${dateStr} @ ${locStr}` : dateStr;
            }
            
            // If it has a location
            if (chart.location?.city || chart.location?.name) {
              return chart.location.city || chart.location.name;
            }
            
            return defaultLabel;
          };
          
          const chart1Desc = getChartDescription(comparativeMode.chart1, comparativeMode.fieldALabel || 'Chart 1');
          const chart2Desc = getChartDescription(comparativeMode.chart2, comparativeMode.fieldBLabel || 'Chart 2');
          
          return `${relType}: ${chart1Desc} ↔ ${chart2Desc}`;
        }
        
        // Regular dial view - always show actual date/time for proper identification
        const city = location.city || 'Location';
        const dateToShow = selectedDate || new Date();
        const dateStr = dateToShow.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const timeStr = dateToShow.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        // For "Current Sky" saves, include time to distinguish from explicit date selections
        if (!selectedDate) {
          return `${city} - ${dateStr} @ ${timeStr}`;
        }
        return `${city} - ${dateStr}`;
      };

      // Build comprehensive view state including CA data
      // IMPORTANT: Always save the actual timestamp, even for "Current Sky" views
      // This ensures the dial can be restored to the exact moment it was saved
      const currentTimestamp = new Date().toISOString();
      const viewState = {
        // For BCE dates, store the ISO format string; for "Current Sky" store actual current time
        date: extendedDateInfo?.isBCE && extendedDateInfo?.isoDate 
          ? `${extendedDateInfo.isoDate}T${extendedDateInfo.time || '12:00'}:00`
          : selectedDate?.toISOString() || currentTimestamp,
        // Flag to indicate this was originally a "Current Sky" view (no explicit date selection)
        was_current_sky: !selectedDate,
        // Store extended date info for BCE date restoration
        extended_date_info: extendedDateInfo ? {
          isBCE: extendedDateInfo.isBCE,
          astronomicalYear: extendedDateInfo.astronomicalYear,
          isoDate: extendedDateInfo.isoDate,
          time: extendedDateInfo.time
        } : null,
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        city: location.city || null,
        custom_label: generateLabel(), // Descriptive label for the save
        selected_planet: universalPanel?.planet || null,
        selected_aspect_index: null,
        comparison_mode: !!comparativeMode,
        comparison_type: comparativeMode?.relationshipType?.key || null,
        // Capture full CA configuration for restoration
        comparison_config: comparativeMode ? {
          relationshipType: comparativeMode.relationshipType,
          chart1: comparativeMode.chart1 ? {
            type: comparativeMode.chart1.type,
            profileId: comparativeMode.chart1.profileId,
            datetime: comparativeMode.chart1.datetime,
            location: comparativeMode.chart1.location,
            // Include profile name for display after restore
            profile: comparativeMode.chart1.profile ? {
              name: comparativeMode.chart1.profile.name
            } : null
          } : null,
          chart2: comparativeMode.chart2 ? {
            type: comparativeMode.chart2.type,
            profileId: comparativeMode.chart2.profileId,
            datetime: comparativeMode.chart2.datetime,
            location: comparativeMode.chart2.location,
            // Include profile name for display after restore
            profile: comparativeMode.chart2.profile ? {
              name: comparativeMode.chart2.profile.name
            } : null
          } : null,
          fieldALabel: comparativeMode.fieldALabel,
          fieldBLabel: comparativeMode.fieldBLabel
        } : null,
        comparison_profile_id: comparativeMode?.chart1?.profileId || comparativeMode?.chart2?.profileId || null,
        comparison_location: comparativeMode?.chart2?.location ? {
          latitude: comparativeMode.chart2.location.latitude,
          longitude: comparativeMode.chart2.location.longitude,
          city: comparativeMode.chart2.location.city
        } : null,
        // Capture Multi-View (Global/Temporal) and Time Lapse configs
        multi_view_config: multiViewConfig ? {
          mode: multiViewConfig.mode,
          primaryLocation: multiViewConfig.primaryLocation,
          locations: multiViewConfig.locations,
          timePoints: multiViewConfig.timePoints
        } : null,
        time_lapse_config: timeLapseConfig ? {
          startDate: timeLapseConfig.startDate,
          endDate: timeLapseConfig.endDate,
          steps: timeLapseConfig.steps,
          location: timeLapseConfig.location
        } : null,
        show_aspects_panel: !!showAspectsPanel,
        show_synthesis_panel: !!showComparisonPanel
      };

      // Get auth token for cross-origin fallback
      const authToken = localStorage.getItem('tod_auth_token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
      };

      const response = await fetch(`${API}/history`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ view_state: viewState, tier })
      });

      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        toast.success('View saved', { 
          description: `"${data.label}" saved to ${tier === 'starred' ? 'Starred' : tier}`
        });
      } else if (response.status === 401) {
        toast.error('Sign in required', { 
          description: 'Create an account to save your views'
        });
        setShowAuthModal(true);
      } else {
        const errText = await response.text();
        logger.error('Save failed:', response.status, errText);
        toast.error('Failed to save view');
      }
    } catch (err) {
      logger.error('Save error:', err);
      toast.error('Failed to save view');
    }
  };

  // Load view state from history - restores full CA state
  const loadViewState = useCallback(async (viewState) => {
    // Get the date to restore - use saved date, or fallback to created_at for old "Current Sky" saves
    const dateToRestore = viewState.date || viewState.created_at || null;
    
    // Set date - handle both regular dates and BCE dates with extended info
    if (dateToRestore) {
      // Check if we have extended date info for BCE dates
      if (viewState.extended_date_info?.isBCE) {
        // Restore BCE date with extended info
        setExtendedDateInfo({
          isBCE: viewState.extended_date_info.isBCE,
          astronomicalYear: viewState.extended_date_info.astronomicalYear,
          isoDate: viewState.extended_date_info.isoDate,
          time: viewState.extended_date_info.time
        });
        // For BCE dates, create a proxy date for the dial display
        // The actual BCE handling is done via extendedDateInfo
        const proxyDate = new Date(dateToRestore);
        setSelectedDate(proxyDate);
      } else {
        // Regular date - ensure we restore the exact saved timestamp
        setSelectedDate(new Date(dateToRestore));
        setExtendedDateInfo(null);
      }
    } else {
      // No date available at all - this shouldn't happen with the fallback logic
      // but keep as safety net
      setSelectedDate(null);
      setExtendedDateInfo(null);
    }

    // Set location
    if (viewState.latitude && viewState.longitude) {
      setLocation({
        latitude: viewState.latitude,
        longitude: viewState.longitude,
        city: viewState.city || 'Unknown',
        state: viewState.state || '',
        country: viewState.country || ''
      });
    }
    
    // Clear cache to force re-fetch with new conditions
    cosmicCache.clear();

    // Clear any open panels first
    setUniversalPanel(null);
    setModulationPanel(null);
    setShowComparisonPanel(false);
    // Only clear comparative mode if we're NOT restoring a comparison
    if (!viewState.comparison_config) {
      setComparativeMode(null);
    }
    setMultiViewConfig(null);
    setTimeLapseConfig(null);

    // Restore Multi-View (Global/Temporal) if saved
    if (viewState.multi_view_config) {
      setMultiViewConfig(viewState.multi_view_config);
      toast.info(`Restored ${viewState.multi_view_config.mode === 'global' ? 'Global View' : 'Temporal View'}`);
      return; // Multi-view is a separate mode
    }

    // Restore Time Lapse if saved
    if (viewState.time_lapse_config) {
      setTimeLapseConfig(viewState.time_lapse_config);
      toast.info('Restored Time Lapse view');
      return; // Time Lapse is a separate mode
    }

    // Restore Comparative Analysis mode if saved - use handleActivateComparison to properly fetch data
    if (viewState.comparison_config) {
      const config = viewState.comparison_config;
      logger.log('[LoadViewState] Restoring comparison config:', config);
      
      // Re-activate the comparison through the proper handler to fetch chart data
      await handleActivateComparison({
        relationshipType: config.relationshipType,
        chart1: config.chart1,
        chart2: config.chart2,
        fieldALabel: config.fieldALabel,
        fieldBLabel: config.fieldBLabel
      });
      
      // Small delay to ensure React state updates propagate before opening panel
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setShowComparisonPanel(true);
      toast.info(`Restored ${config.relationshipType?.label || 'Comparison'} view`);
    }
  }, [handleActivateComparison]);

  // View user's birthday climate (natal chart)
  const viewBirthdayClimate = () => {
    if (!user?.natal_profile) {
      toast.error('No natal profile found');
      return;
    }
    
    const natal = user.natal_profile;
    // Set the date to user's birth date
    const birthDate = new Date(natal.birth_date + 'T' + (natal.birth_time || '12:00'));
    setSelectedDate(birthDate);
    
    // Set location to birth location
    if (natal.birth_location) {
      setLocation({
        latitude: natal.birth_location.latitude,
        longitude: natal.birth_location.longitude,
        city: natal.birth_location.city || 'Birth Location',
        state: natal.birth_location.state || '',
        country: natal.birth_location.country || ''
      });
    }
    
    setShowUserMenu(false);
    toast.success('Viewing your birthday climate', {
      description: `${birthDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
    });
  };

  // Download all history as JSON
  const downloadHistory = async () => {
    setShowUserMenu(false);
    
    try {
      toast.info('Preparing download...');
      
      const authToken = localStorage.getItem('tod_auth_token');
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      
      const response = await fetch(`${API}/history/export`, {
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData = {};
        try { errorData = JSON.parse(errorText); } catch (e) { /* ignore parse error */ }
        throw new Error(errorData.detail || 'Failed to export history');
      }
      
      const dataText = await response.text();
      const data = JSON.parse(dataText);
      
      if (!data.entries || data.entries.length === 0) {
        toast.info('No history to download', { description: 'Save some views first!' });
        return;
      }
      
      // Create and trigger download
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `onescent-dial-history-${new Date().toISOString().slice(0, 10)}.json`;
      
      document.body.appendChild(link);
      
      // Use a small delay to ensure the link is in the DOM
      await new Promise(resolve => setTimeout(resolve, 100));
      
      link.click();
      
      // Cleanup after a delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 250);
      
      toast.success('History downloaded!', {
        description: `${data.entry_count} entries exported`
      });
      
    } catch (err) {
      logger.error('Download history error:', err);
      toast.error('Failed to download history', { description: err.message });
    }
  };

  // Delete all history
  const deleteAllHistory = async () => {
    if (!window.confirm('Are you sure you want to delete ALL your saved history? This includes dial views and saved syntheses. This cannot be undone.')) {
      return;
    }
    
    setShowUserMenu(false);
    
    try {
      toast.info('Deleting history...');
      
      // Clear ALL localStorage for syntheses (all tiers)
      localStorage.removeItem('tod_synthesis_saves');
      localStorage.removeItem('tod_synthesis_notes');
      localStorage.removeItem('tod_search_history');
      
      const authToken = localStorage.getItem('tod_auth_token');
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      
      // Delete all history at once (no tier parameter = delete all)
      const response = await fetch(`${API}/api/history`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData = {};
        try { errorData = JSON.parse(errorText); } catch (e) { /* ignore parse error */ }
        throw new Error(errorData.detail || 'Failed to delete history');
      }
      
      const resultText = await response.text();
      const result = JSON.parse(resultText);
      
      toast.success('All history cleared!', {
        description: `${result.deleted_count || 0} dial views deleted, all syntheses cleared`
      });
      
    } catch (err) {
      logger.error('Delete history error:', err);
      // Even if backend fails, localStorage was cleared
      toast.warning('Partial clear', { 
        description: 'Local storage cleared. Backend sync may have failed.' 
      });
    }
  };

  // Track which chart's data to show in panels when in comparative mode
  const [panelChartContext, setPanelChartContext] = useState(null); // null | 1 | 2

  // Universal element click handler
  const handleElementClick = (elementType, originalElementKey, extraData = null) => {
    // Determine which chart's context to use
    const chartField = extraData?.field;
    if (chartField && comparativeMode) {
      setPanelChartContext(chartField);
    } else {
      setPanelChartContext(null);
    }
    
    // Allow elementKey to be modified for qualified lookups
    let elementKey = originalElementKey;
    
    // Handle modulation types (open ModulationDetailPanel)
    if (elementType === 'modulation' || elementType === 'stellium') {
      setModulationPanel({
        type: elementType,
        data: extraData || { description: elementKey }
      });
      return;
    }
    
    // Handle ingress clicks - open dedicated IngressOverlay
    if (elementType === 'ingress' && extraData) {
      setIngressOverlay(extraData);
      return;
    }
    
    // Handle CiH phase clicks - open PhaseInfoPanel
    if (elementType === 'phase') {
      const phaseName = elementKey?.toUpperCase();
      if (['NOURISH', 'ACTIVATE', 'FULFILL', 'INTEGRATE'].includes(phaseName)) {
        setShowPhasePanel(phaseName);
      } else {
        // Fallback: open Aspects panel
        setShowComparisonPanel(false);
        setShowAspectsPanel(true);
      }
      return;
    }
    
    if (!interpretations) {
      logger.warn('Interpretations not yet loaded');
      return;
    }
    
    let interpretation = null;
    
    switch (elementType) {
      case 'planet':
        interpretation = interpretations.planets?.[elementKey.toLowerCase()];
        break;
      case 'zodiac':
        // Handle case-insensitive lookup (backend may return UPPERCASE)
        interpretation = interpretations.zodiac?.[elementKey] || 
          interpretations.zodiac?.[elementKey.charAt(0).toUpperCase() + elementKey.slice(1).toLowerCase()];
        break;
      case 'season':
        // All seasons use their interpretation directly by name
        // Polar: "Light Returns", "Polar Day", etc.
        // Equatorial: "Wet (Primary)", "Dry (Secondary)", etc.
        // Temperate: "Winter", "Spring", "Summer", "Autumn"
        interpretation = interpretations.seasons?.[elementKey] ||
          interpretations.seasons?.[elementKey.charAt(0).toUpperCase() + elementKey.slice(1).toLowerCase()];
        break;
      case 'sabbat':
        interpretation = interpretations.sabbats?.[elementKey];
        break;
      case 'aspect':
        interpretation = interpretations.aspects?.[elementKey];
        break;
      case 'lunar_phase':
        interpretation = interpretations.lunar_phases?.[elementKey];
        break;
      default:
        break;
    }
    
    if (interpretation) {
      setUniversalPanel({ elementType, elementKey, interpretation, chartContext: chartField });
      // Close modulation panel if open
      setModulationPanel(null);
      // Close comparison overlay if open (user navigated to detail)
      if (showComparisonPanel) {
        setShowComparisonPanel(false);
      }
    } else {
      console.warn(`No interpretation found for ${elementType}: ${elementKey}`);
    }
  };

  // Navigate between panels
  const handlePanelNavigate = (elementType, elementKey, extraData = null) => {
    // Handle modulation/retrograde type - open CO panel and navigate to modulation overlay
    if (elementType === 'modulation') {
      // Close current panel and open CO panel with modulation overlay
      setUniversalPanel(null);
      // Set initial overlay for AspectsPanel to show the modulation overlay
      setAspectsPanelInitialOverlay({ 
        type: 'modulation', 
        flag: extraData,
        label: extraData?.description || extraData?.name || 'Field Condition'
      });
      setShowAspectsPanel(true);
      return;
    }
    // Handle ingress navigation - open dedicated overlay
    if (elementType === 'ingress' && extraData) {
      setUniversalPanel(null);
      setIngressOverlay(extraData);
      return;
    }
    handleElementClick(elementType, elementKey, extraData);
  };

  /**
   * Shared synthesis opening handler - used by MyNotesPanel and SearchHistory
   * Handles opening specific synthesis panels based on synthesis type
   */
  const handleOpenSynthesis = useCallback((synthesisType, synthesisKey, additionalData) => {
    logger.log('[App] Opening synthesis:', synthesisType, synthesisKey, additionalData);
    
    if (synthesisType === 'cosmic_climate') {
      // Main CO synthesis - open the CO panel to show it
      setShowAspectsPanel(true);
      // Scroll to the synthesis after panel opens
      setTimeout(() => {
        const synthesisElement = document.querySelector('[data-testid="cih-synthesis-container"]');
        if (synthesisElement) {
          synthesisElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight animation
          synthesisElement.classList.add('ring-2', 'ring-tod-brass', 'ring-opacity-50');
          setTimeout(() => {
            synthesisElement.classList.remove('ring-2', 'ring-tod-brass', 'ring-opacity-50');
          }, 3000);
        }
      }, 300);
    } else if (synthesisType === 'planet') {
      // Open the planet info panel directly (UniversalInfoPanel)
      const planetKey = synthesisKey?.toLowerCase();
      const interpretation = interpretations?.planets?.[planetKey];
      
      if (interpretation) {
        setUniversalPanel({ 
          elementType: 'planet', 
          elementKey: planetKey, 
          interpretation,
          chartContext: null,
          isRestoredSynthesis: true,
          restoredContext: additionalData
        });
        toast.success(`Synthesis restored: ${synthesisKey}`, { 
          description: 'Viewing saved synthesis',
          duration: 3000 
        });
      } else {
        setShowAspectsPanel(true);
        setTimeout(() => {
          const interp = interpretations?.planets?.[planetKey];
          if (interp) {
            setUniversalPanel({ 
              elementType: 'planet', 
              elementKey: planetKey, 
              interpretation: interp,
              chartContext: null,
              isRestoredSynthesis: true,
              restoredContext: additionalData
            });
          } else {
            toast.info(`View ${synthesisKey} in the Orientation panel`, { duration: 3000 });
          }
        }, 500);
      }
    } else if (synthesisType === 'archetype' || synthesisType === 'planetArchetype') {
      const archetypeKey = synthesisKey?.toLowerCase();
      logger.log('[App] Opening planet archetype deep dive:', archetypeKey);
      setDeepDivePanel({ isOpen: true, type: 'planetArchetype', itemKey: archetypeKey });
      toast.success(`Opened ${synthesisKey} archetype view`, { duration: 2000 });
    } else if (synthesisType === 'signArchetype') {
      const archetypeKey = synthesisKey?.toLowerCase();
      logger.log('[App] Opening sign archetype deep dive:', archetypeKey);
      setDeepDivePanel({ isOpen: true, type: 'signArchetype', itemKey: archetypeKey });
      toast.success(`Opened ${synthesisKey} archetype view`, { duration: 2000 });
    } else if (synthesisType === 'eclipse') {
      // Eclipse synthesis - open the CO panel with eclipse overlay
      logger.log('[App] Opening eclipse overlay from saved synthesis');
      setAspectsPanelInitialOverlay({ type: 'eclipse', data: additionalData });
      setShowAspectsPanel(true);
      toast.success(`Eclipse synthesis restored`, { 
        description: 'Opening eclipse view',
        duration: 3000 
      });
    } else if (synthesisType === 'stellium') {
      // Stellium synthesis - open the CO panel with stellium overlay
      logger.log('[App] Opening stellium overlay from saved synthesis', additionalData);
      setAspectsPanelInitialOverlay({ type: 'stellium', sign: additionalData?.sign, planets: additionalData?.planets });
      setShowAspectsPanel(true);
      toast.success(`Stellium synthesis restored`, { 
        description: additionalData?.sign ? `${additionalData.sign} stellium` : 'Opening stellium view',
        duration: 3000 
      });
    } else if (['element', 'temporal_layer', 'aspect', 'modulation'].includes(synthesisType)) {
      setShowAspectsPanel(true);
      setTimeout(() => {
        if (interpretations) {
          handleElementClick(synthesisType, synthesisKey, additionalData);
        } else {
          toast.info('Opening synthesis view', {
            description: `${synthesisType}: ${synthesisKey}`
          });
        }
      }, 500);
    } else if (synthesisType === 'zodiac') {
      const zodiacKey = synthesisKey?.charAt(0).toUpperCase() + synthesisKey?.slice(1).toLowerCase();
      const interpretation = interpretations?.zodiac?.[zodiacKey] || interpretations?.zodiac?.[synthesisKey];
      
      if (interpretation) {
        setUniversalPanel({ 
          elementType: 'zodiac', 
          elementKey: zodiacKey, 
          interpretation,
          chartContext: null,
          isRestoredSynthesis: true,
          restoredContext: additionalData
        });
        toast.success(`Synthesis restored: ${zodiacKey}`, { 
          description: 'Viewing saved synthesis',
          duration: 3000 
        });
      } else {
        setDeepDivePanel({ isOpen: true, type: 'zodiac', itemKey: synthesisKey, isRestoredSynthesis: true });
      }
    } else if (['phase', 'lunar_phase', 'season', 'sabbat', 'modality', 'bodyCorrespondence', 'placeInfluence', 'solarWeather', 'nodalAxis'].includes(synthesisType)) {
      setDeepDivePanel({ isOpen: true, type: synthesisType, itemKey: synthesisKey, isRestoredSynthesis: true });
      toast.success(`Synthesis restored: ${synthesisKey}`, { 
        description: 'Viewing saved synthesis',
        duration: 3000 
      });
    } else if (synthesisType === 'spacio_temporal' || synthesisType === 'climate') {
      setShowAspectsPanel(true);
      setTimeout(() => {
        handleElementClick('climate', synthesisKey || 'daylight', additionalData);
      }, 500);
    } else {
      // Unknown type - just open the CO panel
      logger.warn('[App] Unknown synthesis type:', synthesisType);
      setShowAspectsPanel(true);
      toast.info('Synthesis loaded', {
        description: `View the ${synthesisType || 'synthesis'} in the Orientation panel`
      });
    }
  }, [interpretations, handleElementClick, setShowAspectsPanel, setUniversalPanel, setDeepDivePanel]);

  // Close all overlays/panels (for Escape key)
  const closeAllPanels = useCallback(() => {
    if (universalPanel) setUniversalPanel(null);
    else if (showAspectsPanel) setShowAspectsPanel(false);
    else if (showGlossaryPanel) setShowGlossaryPanel(false);
    else if (showProfileManager) setShowProfileManager(false);
    else if (showQuickStatus) setShowQuickStatus(false);
    else if (showKeyboardShortcuts) setShowKeyboardShortcuts(false);
    else if (modulationPanel) setModulationPanel(null);
    else if (showComparisonPanel) setShowComparisonPanel(false);
    else if (showComparativeSetup) setShowComparativeSetup(false);
    else if (showDatePicker) setShowDatePicker(false);
    else if (showLocationPicker) setShowLocationPicker(false);
    else if (showAuthModal) setShowAuthModal(false);
  }, [universalPanel, showAspectsPanel, showGlossaryPanel, showProfileManager, showQuickStatus, showKeyboardShortcuts, modulationPanel, showComparisonPanel, showComparativeSetup, showDatePicker, showLocationPicker, showAuthModal]);

  // Keyboard navigation hook
  const { focusedElement, isKeyboardActive } = useKeyboardNavigation({
    enabled: true,
    onElementSelect: handleElementClick,
    onThemeChange: cycleTheme,
    onGlossaryToggle: () => setShowGlossaryPanel(prev => !prev),
    onProfileToggle: () => isAuthenticated ? setShowProfileManager(prev => !prev) : setShowAuthModal(true),
    onHelpToggle: () => setShowKeyboardShortcuts(prev => !prev),
    onEscape: closeAllPanels,
    currentZodiac: cosmicData?.positions?.sun?.zodiac_sign,
    currentSeason: cosmicData?.season?.season
  });

  // Helper to open ProfileManager with specific tab
  const openProfileManager = (tab = 'profiles') => {
    setProfileManagerInitialTab(tab);
    setShowProfileManager(true);
    setShowUserMenu(false);
  };

  // Listen for openProfileManager events from other components
  useEffect(() => {
    const handleOpenProfileManager = (event) => {
      const tab = event.detail?.tab || 'profiles';
      openProfileManager(tab);
    };
    
    window.addEventListener('openProfileManager', handleOpenProfileManager);
    return () => window.removeEventListener('openProfileManager', handleOpenProfileManager);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for openSearchHistory events from other components
  useEffect(() => {
    const handleOpenSearchHistory = (event) => {
      const tab = event.detail?.tab || 'places';
      setSearchHistoryInitialTab(tab);
      setShowSearchHistory(true);
    };
    
    window.addEventListener('openSearchHistory', handleOpenSearchHistory);
    return () => window.removeEventListener('openSearchHistory', handleOpenSearchHistory);
  }, []);

  // Listen for openMyNotes events from other components (e.g., after saving a note)
  // Scrolls to the always-visible My Notes panel
  useEffect(() => {
    const handleOpenMyNotes = () => {
      const panel = document.getElementById('my-notes-panel-wrapper');
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Add a brief highlight effect
        panel.classList.add('ring-2', 'ring-tod-accent-purple', 'ring-offset-2');
        setTimeout(() => {
          panel.classList.remove('ring-2', 'ring-tod-accent-purple', 'ring-offset-2');
        }, 2000);
      }
    };
    
    window.addEventListener('openMyNotes', handleOpenMyNotes);
    return () => window.removeEventListener('openMyNotes', handleOpenMyNotes);
  }, []);

  // Memoized upcoming events calculation (expensive filter operations)
  const upcomingEvents = useMemo(() => ({
    sabbats: cosmicData?.sabbats,
    eclipses: cosmicData?.eclipse_conditions?.upcoming_eclipses?.filter(e => e.days_until <= 30),
    stelliums: cosmicData?.cih_intelligence?.stelliums,
    retrogrades: cosmicData?.cih_intelligence?.planetary_flags?.filter(f => f.flag_type === 'retrograde')
  }), [cosmicData?.sabbats, cosmicData?.eclipse_conditions?.upcoming_eclipses, cosmicData?.cih_intelligence?.stelliums, cosmicData?.cih_intelligence?.planetary_flags]);

  // Memoized CyKL chat context (prevents unnecessary re-renders)
  const cyKLContext = useMemo(() => ({
    currentPhase: cosmicData?.cih_intelligence?.dominant_phase,
    phaseConfluence: cosmicData?.cih_intelligence?.phase_confluence,
    location: location,
    cityName: location?.city || 'Unknown',
    hemisphere: location?.lat > 0 ? 'Northern' : 'Southern',
    cosmicData: cosmicData,
    moonPhase: cosmicData?.positions?.moon?.phase,
    moonSign: cosmicData?.positions?.moon?.sign,
    sunSign: cosmicData?.positions?.sun?.sign,
    upcomingEvents: upcomingEvents,
    selectedDate: selectedDate,
    currentPanel: showAspectsPanel ? 'aspects' : showComparativeSetup ? 'comparison' : 'main',
    isLoggedIn: !!user
  }), [cosmicData, location, upcomingEvents, selectedDate, showAspectsPanel, showComparativeSetup, user]);

  return (
    <div className="dial-container" data-testid={BRANDING_META.testIds.dialApp}>
      {/* Cosmic Background Animations */}
      <Suspense fallback={null}>
        <CosmicBackground />
      </Suspense>
      
      {/* SEO Meta Tags */}
      <SEOHead 
        dialState={{
          location: {
            city: location.city,
            latitude: location.lat,
            longitude: location.lng
          },
          date: selectedDate,
          cosmicData: cosmicData || {}
        }}
      />
      
      {/* Cosmic Events Ticker - Fixed at very top of page, ALWAYS VISIBLE */}
      <Suspense fallback={null}>
        <div className="fixed top-0 left-0 right-0 z-[90]">
          <CosmicTicker 
            onNavigateToTime={(timestamp, eventInfo = {}) => {
              // Navigate dial to the event's time
              const eventDate = new Date(timestamp);
              setSelectedDate(eventDate);
              cosmicCache.forceRefresh(); // Clear cache to load new data
              // Close any open modals/panels when navigating
              setComparativeMode(false);
              
              // Open the appropriate panel based on event type
              // Use longer delay to ensure data is loaded before opening overlay
              setTimeout(() => {
                switch (eventInfo.eventType) {
                  case 'retrograde':
                  case 'signChange':
                  case 'station':
                    // Open planet info via handleElementClick which opens in AspectsPanel (Universal Info)
                    if (eventInfo.planetName) {
                      handleElementClick('planet', eventInfo.planetName);
                    }
                    break;
                  case 'eclipse':
                    // Open AspectsPanel with eclipse overlay
                    setAspectsPanelInitialOverlay({ type: 'eclipse' });
                    setShowAspectsPanel(true);
                    break;
                  case 'sabbat':
                    // Open sabbat info via handleElementClick (same as clicking sabbat ring on disc)
                    if (eventInfo.sabbatKey || eventInfo.sabbatName) {
                      handleElementClick('sabbat', eventInfo.sabbatKey || eventInfo.sabbatName);
                    }
                    break;
                  case 'stellium':
                    // Open AspectsPanel with stellium overlay
                    setAspectsPanelInitialOverlay({ type: 'stellium', sign: eventInfo.sign, planets: eventInfo.planets });
                    setShowAspectsPanel(true);
                    break;
                  case 'upcomingAspect':
                    // Open planet info for the first planet in the aspect
                    if (eventInfo.aspectData) {
                      handleElementClick('planet', eventInfo.aspectData.body1);
                    }
                    break;
                  case 'ingress':
                    // Open dedicated IngressOverlay if we have ingress data; fallback to planet info
                    if (eventInfo.ingressData) {
                      setIngressOverlay(eventInfo.ingressData);
                    } else if (eventInfo.planetName) {
                      handleElementClick('planet', eventInfo.planetName);
                    }
                    break;
                  default:
                    // Just navigate without opening panel
                    break;
                }
              }, 800); // Allow time for cosmic data to refresh
              
              toast.success('Navigating to event', {
                description: eventDate.toLocaleDateString('en-US', { 
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: 'numeric', minute: '2-digit'
                }),
                duration: 2000
              });
            }}
            isVisible={true}
          />
        </div>
      </Suspense>
      
      {/* In-App Notification Banner */}
      {isAuthenticated && (
        <Suspense fallback={null}>
          <NotificationBanner 
            isAuthenticated={isAuthenticated} 
            onNavigateToDate={(date) => {
              setSelectedDate(date);
              cosmicCache.forceRefresh();
              toast.success('Navigating to event', {
                description: date.toLocaleDateString('en-US', { 
                  month: 'short', day: 'numeric', year: 'numeric'
                }),
                duration: 2000
              });
            }}
          />
        </Suspense>
      )}
      
      {/* Background Layers */}
      <div className="nebula-background"></div>
      <div className="stars-background"></div>

      {/* Header Controls - Responsive */}
      <div className="header-controls">
        <div className="flex flex-col items-start">
          <div className="bg-[var(--tod-bg-secondary)] border border-[var(--tod-brass)]/30 p-2 md:p-4 rounded-sm">
            <h1 
              className="header-title text-lg sm:text-xl md:text-2xl lg:text-3xl font-black tracking-tight uppercase brass-glow cursor-pointer hover:text-tod-gold transition-colors"
              style={{ fontFamily: 'Cinzel Decorative, serif' }}
              data-testid="app-title"
              onClick={() => window.open('https://onescence.com', '_blank', 'noopener,noreferrer')}
              title="Visit Onescence - Free Periodical"
            >
              The Onescent Dial
            </h1>
            <p className="header-subtitle text-[0.55rem] sm:text-xs md:text-sm opacity-70 tracking-widest">
              Based on the Cycles in Harmony Cosmology
            </p>
            {/* PWA Install — inside title box for overlay protection */}
            {!isInstalled && (
              <button
                onClick={async () => {
                  if (canInstall) {
                    const accepted = await promptInstall();
                    if (accepted) {
                      const { toast } = await import('sonner');
                      toast.success('App installed!');
                    }
                  } else if (isIOS) {
                    const { toast } = await import('sonner');
                    toast.info('Tap the Share button, then "Add to Home Screen"', { duration: 5000 });
                  } else {
                    const { toast } = await import('sonner');
                    toast.info('Use your browser menu to install this app', { duration: 4000 });
                  }
                }}
                data-testid="pwa-install-quick-btn"
                className="mt-1.5 flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-[var(--tod-brass)]/20 text-[var(--tod-brass)] text-[10px] tracking-wide hover:border-[var(--tod-brass)]/50 hover:bg-[var(--tod-brass)]/10 transition-all opacity-70 hover:opacity-100"
                title="Install app for fullscreen experience"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Install App
              </button>
            )}
          </div>
        </div>

        {/* Right sidebar buttons - with edge padding to prevent back swipe */}
        <div className="flex flex-col gap-1 sm:gap-2 edge-safe-right" role="navigation" aria-label="Main controls">
          {/* Select Date Button */}
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className={getButtonClasses('primary')}
            data-testid="open-date-picker-btn"
            aria-label="Select date and time"
            aria-expanded={showDatePicker}
          >
            <Calendar size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
            <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.selectDate.label}</span>
          </button>

          {/* Location Button */}
          <button
            onClick={() => setShowLocationPicker(!showLocationPicker)}
            className={getButtonClasses('secondary')}
            data-testid="open-location-btn"
            aria-label={`Change location. Currently: ${location.city || 'Unknown'}`}
            aria-expanded={showLocationPicker}
          >
            <MapPin size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
            <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.location.label}</span>
          </button>

          {/* Orientation Button */}
          <button
            onClick={() => setShowAspectsPanel(!showAspectsPanel)}
            className={getButtonClasses('primary')}
            data-testid="open-aspects-btn"
            aria-label="View contextual orientation and interpretations"
            aria-expanded={showAspectsPanel}
          >
            <Info size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
            <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.orientation.label}</span>
          </button>
          
          {/* Compare Button - Shows "Compare" when not in mode, "Exit" when active */}
          <button
            onClick={() => comparativeMode ? exitComparativeMode() : setShowComparativeSetup(true)}
            className={`${getButtonClasses('base')} ${
              comparativeMode 
                ? 'border-[var(--tod-purple)] text-[var(--tod-lavender)] hover:bg-[var(--tod-purple)]/20' 
                : 'border-[var(--tod-purple)]/50 text-[var(--tod-purple)]/70 hover:border-[var(--tod-purple)] hover:text-[var(--tod-lavender)]'
            }`}
            data-testid="split-view-toggle"
            title={comparativeMode ? 'Exit Comparative Mode' : 'Enter Comparative Mode'}
            aria-label={comparativeMode ? 'Exit comparative analysis mode' : 'Enter comparative analysis mode'}
            aria-pressed={!!comparativeMode}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 transition-transform duration-500 ${comparativeMode ? 'rotate-180' : ''}`}
            >
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10"/>
              <path d="M12 2c3 3 4.5 6.5 4.5 10s-1.5 7-4.5 10"/>
              <path d="M2 12h10"/>
              <circle cx="17" cy="12" r="5" strokeDasharray="2 2"/>
            </svg>
            <span className="hidden md:inline">
              {comparativeMode ? OPERATIONAL_PREFERENCES.sidebarButtons.compare.labelActive : OPERATIONAL_PREFERENCES.sidebarButtons.compare.label}
            </span>
          </button>
          
          {/* Synthesis Button - Only visible in Comparative Mode */}
          {comparativeMode && (
            <button
              onClick={() => setShowComparisonPanel(true)}
              className={getButtonClasses('purple')}
              data-testid="open-synthesis-btn"
              title="Open Field Synthesis"
              aria-label="Open field synthesis panel"
            >
              <Layers size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
              <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.synthesis.label}</span>
            </button>
          )}
          
          {/* Save Button */}
          {user ? (
            <button
              onClick={() => saveToHistory('session')}
              onContextMenu={(e) => {
                e.preventDefault();
                saveToHistory('starred');
              }}
              className={getButtonClasses('greenMuted')}
              data-testid="save-history-btn"
              title="Save current view (right-click to star)"
              aria-label="Save current view to history"
            >
              <Save size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
              <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.save.label}</span>
            </button>
          ) : (
            <button
              onClick={() => {
                toast.info('Sign in to save views', { description: 'Create a free account to save and manage your history' });
                setShowAuthModal(true);
              }}
              className={getButtonClasses('greenDisabled')}
              data-testid="save-history-btn-disabled"
              title="Sign in to save views"
              aria-label="Sign in to save views"
            >
              <Save size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
              <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.save.label}</span>
            </button>
          )}
          
          {/* History Button */}
          {user ? (
            <button
              onClick={() => setShowSearchHistory(true)}
              className={getButtonClasses('blue')}
              data-testid="view-history-btn"
              title="View saved history"
              aria-label="Open view history"
            >
              <History size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
              <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.history.label}</span>
            </button>
          ) : (
            <button
              onClick={() => {
                toast.info('Sign in to view history', { description: 'Create a free account to access your saved views' });
                setShowAuthModal(true);
              }}
              className={getButtonClasses('blueDisabled')}
              data-testid="view-history-btn-disabled"
              title="Sign in to view history"
              aria-label="Sign in to view history"
            >
              <History size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
              <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.history.label}</span>
            </button>
          )}
          
          {/* Share Button */}
          <button
            onClick={() => setShowExportShare(true)}
            className={getButtonClasses('purpleMuted')}
            data-testid="export-share-btn"
            title="Export or share this view"
            aria-label="Export or share"
          >
            <Share2 size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
            <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.share.label}</span>
          </button>
          
          {/* Reference Button */}
          <button
            onClick={() => setShowGlossaryPanel(true)}
            className={getButtonClasses('muted')}
            data-testid="glossary-btn"
            title="CiH Reference - Glossary & FAQ"
            aria-label="Open CiH glossary and FAQ reference"
          >
            <BookOpen size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
            <span className="hidden md:inline">Reference</span>
          </button>
          
          {/* Tutorial Button */}
          <button
            onClick={handleRestartTutorial}
            className={getButtonClasses('muted')}
            data-testid="help-tutorial-btn"
            title="Show Tutorial"
            aria-label="Show tutorial guide"
          >
            <HelpCircle size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
            <span className="hidden md:inline">{OPERATIONAL_PREFERENCES.sidebarButtons.tutorial.label}</span>
          </button>
          
          {/* User Account Button */}
          <div className="relative w-full">
            {authLoading ? (
              /* Show loading placeholder while checking auth */
              <button
                className={getButtonClasses('muted')}
                disabled
                aria-label="Checking authentication..."
              >
                <User size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 animate-pulse" aria-hidden="true" />
                <span className="hidden md:inline">...</span>
              </button>
            ) : isAuthenticated ? (
              <>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={getButtonClasses('green')}
                  data-testid="user-menu-btn"
                  title={user?.name || 'Account'}
                  aria-label={`Account menu for ${user?.name || 'user'}`}
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex-shrink-0" aria-hidden="true" />
                  ) : (
                    <User size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
                  )}
                  <span className="hidden md:inline truncate">{user?.name?.split(' ')[0]}</span>
                </button>
                
                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <>
                    {/* Backdrop to close menu */}
                    <div 
                      className="fixed inset-0 z-[55]" 
                      onClick={() => setShowUserMenu(false)}
                    />
                    {/* Menu content - Fixed positioning on all screens for proper viewport handling */}
                    <div 
                      className="fixed inset-x-3 sm:inset-x-auto sm:right-3 sm:w-64 z-[60] overflow-y-auto rounded-lg shadow-xl bg-[var(--tod-bg-primary)] border border-[var(--tod-brass)]/50"
                      style={{
                        top: '56px',
                        maxHeight: 'calc(100vh - 120px)', // Account for header and footer
                      }}
                      role="menu"
                      aria-label="User account options"
                    >
                      {/* User Info */}
                      <div className="p-3 border-b border-[var(--tod-brass)]/20">
                        <div className="text-sm text-[var(--tod-text)] font-medium">{user?.name}</div>
                        <div className="text-xs text-[var(--tod-text-secondary)]">{user?.email}</div>
                        <div className="text-[10px] text-[var(--tod-green)] mt-1">
                          {profileCount}/{user?.max_profiles || 3} profiles
                        </div>
                      </div>
                      
                      {/* Quick Actions */}
                      <div className="border-b border-[var(--tod-brass)]/20">
                        <button
                          onClick={viewBirthdayClimate}
                          className={getMenuItemClasses('default')}
                          data-testid="view-birthday-btn"
                          role="menuitem"
                        >
                          <Star size={15} className="text-[var(--tod-gold)]" />
                          {OPERATIONAL_PREFERENCES.userMenu.viewOriginalOrientation.label}
                        </button>
                        
                        {/* Profile Management Section */}
                        <div className="py-1">
                          <button
                            onClick={() => openProfileManager('profiles')}
                            className={getMenuItemClasses('default')}
                            data-testid="manage-profiles-btn"
                            role="menuitem"
                          >
                            <Users size={15} className="text-[var(--tod-brass)]" />
                            {OPERATIONAL_PREFERENCES.userMenu.manageProfiles.label}
                          </button>
                          
                          {/* Quick Links Row */}
                          <div className="flex gap-1 px-3 pb-2">
                            <button
                              onClick={() => openProfileManager('credits')}
                              className="flex-1 px-2 py-1.5 text-[10px] bg-[var(--tod-gold)]/10 hover:bg-[var(--tod-gold)]/20 border border-[var(--tod-gold)]/30 hover:border-[var(--tod-gold)]/50 text-[var(--tod-gold)] rounded flex items-center justify-center gap-1.5 transition-colors"
                              data-testid="quick-addons-btn"
                            >
                              <Package size={11} />
                              <span>Add-Ons</span>
                            </button>
                            <button
                              onClick={() => openProfileManager('preferences')}
                              className="flex-1 px-2 py-1.5 text-[10px] bg-[var(--tod-purple)]/10 hover:bg-[var(--tod-purple)]/20 border border-[var(--tod-purple)]/30 hover:border-[var(--tod-purple)]/50 text-[var(--tod-purple)] rounded flex items-center justify-center gap-1.5 transition-colors"
                              data-testid="quick-settings-btn"
                            >
                              <Settings size={11} />
                              <span>Settings</span>
                            </button>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => {
                            // Scroll to the My Notes panel
                            const panel = document.getElementById('my-notes-panel-wrapper');
                            if (panel) {
                              panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              // Add a brief highlight effect
                              panel.classList.add('ring-2', 'ring-tod-accent-purple', 'ring-offset-2');
                              setTimeout(() => {
                                panel.classList.remove('ring-2', 'ring-tod-accent-purple', 'ring-offset-2');
                              }, 2000);
                            }
                            setShowUserMenu(false);
                          }}
                          className={getMenuItemClasses('purple')}
                          data-testid="my-notes-btn"
                          role="menuitem"
                        >
                          <StickyNote size={15} className="text-[var(--tod-accent-purple)]" />
                          <span>My Notes</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            toast.info('Coming Soon', { description: 'Synthesis Studies feature is in development' });
                          }}
                          className={getMenuItemClasses('disabled')}
                          data-testid="synthesis-study-btn"
                          role="menuitem"
                          disabled
                        >
                          <Sparkles size={15} className="text-[var(--tod-purple)]/50" />
                          <span>Create Synthesis Study</span>
                          <span className="ml-auto text-[10px] text-[var(--tod-text-secondary)]/40 uppercase">Soon</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            toast.info('Coming Soon', { description: 'Orientation Calculator is in development' });
                          }}
                          className={getMenuItemClasses('disabled')}
                          data-testid="orientation-calculator-btn"
                          role="menuitem"
                          disabled
                        >
                          <Calculator size={15} className="text-[var(--tod-gold)]/50" />
                          <span>Orientation Calculator</span>
                          <span className="ml-auto text-[10px] text-[var(--tod-text-secondary)]/40 uppercase">Soon</span>
                        </button>
                      </div>
                      
                      {/* Data & History Management */}
                      <div className="border-b border-[var(--tod-brass)]/20">
                        {/* Admin Panel - Only for admin/vii users */}
                        {hasAdminAccess && (
                          <button
                            onClick={() => {
                              setShowAdminPanel(true);
                              setShowUserMenu(false);
                            }}
                            className={getMenuItemClasses('gold')}
                            data-testid="admin-panel-btn"
                            role="menuitem"
                          >
                            <Key size={15} className="text-[var(--tod-gold)]" />
                            {user?.role === 'vii' ? 'Influencer Panel' : 'Admin Panel'}
                          </button>
                        )}
                        
                        <button
                          onClick={() => {
                            retryFetch();
                            toast.success('Refreshing', { description: 'Clearing cache and fetching fresh cosmic data...' });
                            setShowUserMenu(false);
                          }}
                          className={getMenuItemClasses('cyan')}
                          data-testid="refresh-data-btn"
                          role="menuitem"
                        >
                          <RefreshCw size={15} className="text-[var(--tod-cyan)]" />
                          Refresh Data
                          <span className="ml-auto text-[10px] text-[var(--tod-text-secondary)]/60">Clear Cache</span>
                        </button>
                      </div>
                      
                      {/* Account Settings */}
                      <div>
                        <button
                          onClick={() => {
                            setShowChangePassword(true);
                            setShowUserMenu(false);
                          }}
                          className={getMenuItemClasses('default')}
                          data-testid="change-password-btn"
                          role="menuitem"
                        >
                          <KeyRound size={15} className="text-[var(--tod-brass)]" />
                          Change Password
                        </button>
                        
                        <button
                          onClick={() => { 
                            logout(); 
                            setShowUserMenu(false); 
                          }}
                          className={getMenuItemClasses('default')}
                          data-testid="logout-btn"
                          role="menuitem"
                          aria-label="Sign out of your account"
                        >
                          <LogOut size={15} />
                          Sign Out
                        </button>
                        
                        {/* Exit Menu Button */}
                        <button
                          onClick={() => setShowUserMenu(false)}
                          className="w-full px-3 py-2.5 flex items-center gap-3 text-sm text-[var(--tod-text-secondary)] hover:text-[var(--tod-text)] hover:bg-[var(--tod-brass)]/10 transition-colors border-t border-[var(--tod-brass)]/20 mt-1"
                          data-testid="close-user-menu-btn"
                          role="menuitem"
                          aria-label="Close menu"
                        >
                          <X size={15} />
                          Close Menu
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className={getButtonClasses('muted')}
                data-testid="sign-in-btn"
                title="Sign in to save profiles"
                aria-label="Sign in to save profiles"
              >
                <User size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
                <span className="hidden md:inline">Sign In</span>
              </button>
            )}
          </div>
          
          {selectedDate && (
            <button
              onClick={resetToNow}
              className={getButtonClasses('cyan')}
              data-testid="reset-to-now-btn"
            >
              <Clock size={16} className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="hidden md:inline">Now</span>
            </button>
          )}
        </div>
      </div>

      {/* Footer Metadata - Responsive */}
      {/* Main Dial - Viewport Contained with Zoom */}
      <div 
        className={`dial-viewport ${comparativeMode ? 'comparative-mode' : ''}`}
        onWheel={handlePinchZoom}
      >
        {/* Zoom controls moved to ExperienceSettings panel */}
        
        {loading ? (
          <div className="loading-spinner">
            <div 
              className="text-[var(--tod-brass)] text-4xl sm:text-6xl brass-glow animate-spin"
              style={{ animationDuration: '2s' }}
              data-testid="loading-spinner"
            >
              ⚙
            </div>
            <p className="text-[var(--tod-text-secondary)] text-sm mt-4">Loading cosmic data...</p>
            {/* Show retry button after 5 seconds of loading */}
            <button
              onClick={retryFetch}
              className="mt-4 px-4 py-2 border border-[var(--tod-brass)]/50 text-[var(--tod-brass)] text-sm hover:bg-[var(--tod-brass)]/10 transition-colors"
              data-testid="loading-retry-btn"
            >
              Taking too long? Tap to retry
            </button>
          </div>
        ) : apiError ? (
          <div className="loading-spinner">
            <div className="text-[var(--tod-red)] text-2xl mb-4">⚠</div>
            <p className="text-[var(--tod-red)] text-sm mb-4">{apiError}</p>
            <button
              onClick={retryFetch}
              className="px-4 py-2 border border-[var(--tod-brass)]/50 text-[var(--tod-brass)] text-sm hover:bg-[var(--tod-brass)]/10 transition-colors"
              data-testid="error-retry-btn"
            >
              Try Again
            </button>
          </div>
        ) : comparativeMode ? (
          /* Split View - Two Dials Side by Side */
          comparisonLoading || !comparisonDataReady ? (
            <div className="loading-spinner">
              <div 
                className="text-[var(--tod-purple)] text-4xl sm:text-6xl animate-spin"
                style={{ animationDuration: '2s' }}
                data-testid="comparison-loading"
              >
                ⚙
              </div>
              <p className="text-[var(--tod-purple)] text-sm mt-4">Loading comparison data...</p>
              {/* Debug info and retry */}
              <p className="text-[var(--tod-text-secondary)] text-xs mt-2">
                {!cosmicData ? 'Waiting for base cosmic data...' : 'Fetching chart data...'}
              </p>
              <button
                onClick={() => {
                  // If cosmicData is missing, retry that first
                  if (!cosmicData) {
                    retryFetch();
                  } else {
                    // Exit comparison and retry
                    exitComparativeMode();
                    toast.info('Comparison cancelled. Please try again.');
                  }
                }}
                className="mt-4 px-4 py-2 border border-[var(--tod-purple)]/50 text-[var(--tod-purple)] text-sm hover:bg-[var(--tod-purple)]/10 transition-colors"
                data-testid="comparison-retry-btn"
              >
                {!cosmicData ? 'Retry Loading' : 'Cancel & Retry'}
              </button>
            </div>
          ) : (
          <div className="split-view-container" data-testid="split-view">
            {/* Chart 1 - Left */}
            <div className="split-dial split-dial-left" data-testid="split-dial-1">
              <div className="split-dial-label">
                <span className="text-tod-brass">
                  {comparativeMode.chart1?.profile?.name || comparativeMode.fieldALabel || 'Current Sky'}
                </span>
                {comparativeMode.chart1?.datetime && (
                  <span className="text-xs text-tod-text-secondary ml-2">
                    {new Date(comparativeMode.chart1.datetime).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div 
                className="dial-zoom-container"
                style={{ 
                  transform: `scale(${zoomLevel * 0.85})`,
                  transformOrigin: 'center center'
                }}
              >
                <Dial 
                  data={chart1DisplayData} 
                  onPlanetClick={(planet) => handleElementClick('planet', planet, { field: 1 })}
                  onElementClick={(type, key, extra) => handleElementClick(type, key, { ...extra, field: 1 })}
                  onCenterClick={() => {}}
                  hideHints={true}
                />
              </div>
            </div>
            
            {/* Chart 2 - Right */}
            <div className="split-dial split-dial-right" data-testid="split-dial-2">
              <div className="split-dial-label">
                <span className="text-tod-accent-cyan">
                  {comparativeMode.chart2?.profile?.name || comparativeMode.fieldBLabel || 'Chart 2'}
                </span>
                {comparativeMode.chart2?.datetime && (
                  <span className="text-xs text-tod-text-secondary ml-2">
                    {new Date(comparativeMode.chart2.datetime).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div 
                className="dial-zoom-container"
                style={{ 
                  transform: `scale(${zoomLevel * 0.85})`,
                  transformOrigin: 'center center'
                }}
              >
                <Dial 
                  data={chart2DisplayData} 
                  onPlanetClick={(planet) => handleElementClick('planet', planet, { field: 2 })}
                  onElementClick={(type, key, extra) => handleElementClick(type, key, { ...extra, field: 2 })}
                  onCenterClick={() => {}}
                  hideHints={true}
                />
              </div>
            </div>
          </div>
          )
        ) : cosmicData ? (
          <div className="dial-with-context flex flex-col lg:flex-row items-center justify-center gap-4 w-full max-w-full px-3 lg:px-0">
            {/* Notes Panel - order controlled by CSS media queries */}
            <div className="notes-panel-wrapper flex-shrink-0 w-full sm:w-auto" id="my-notes-panel-wrapper">
              <Suspense fallback={<div className="w-full h-[300px] bg-tod-bg-secondary/50 rounded-xl animate-pulse" />}>
                <MyNotesPanel
                  onShowAuth={() => setShowAuthModal(true)}
                  onLoadViewState={(viewState) => {
                    if (viewState.date) {
                      const date = new Date(viewState.date);
                      setSelectedDate(date);
                    }
                    if (viewState.latitude && viewState.longitude) {
                      setLocation({
                        latitude: viewState.latitude,
                        longitude: viewState.longitude,
                        city: viewState.city || 'Unknown',
                        state: viewState.state || '',
                        country: viewState.country || ''
                      });
                    }
                    cosmicCache.clear();
                  }}
                  onOpenSynthesis={handleOpenSynthesis}
                />
              </Suspense>
            </div>
            
            {/* Main Dial - order controlled by CSS media queries */}
            <div 
              id="main-dial"
              className="dial-zoom-container flex-shrink-0 flex flex-col items-center"
              style={{ 
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center'
              }}
              tabIndex={0}
              role="application"
              aria-label="The Onescent Dial - Interactive cosmic visualization. Use arrow keys to navigate, Enter to select."
            >
              {/* Birth Chart Analysis discovery chip — sits just above the dial.
                  Dropdown renders through a React portal into <body> so it escapes
                  this dial-zoom-container's scale-transform stacking context. */}
              <Suspense fallback={null}>
                <div className="mb-2">
                  <BirthChartBanner
                    onOpenAuth={() => setShowAuthModal(true)}
                    onOpenProfileManager={() => setShowProfileManager(true)}
                    onOpenHorizon={() => setShowHorizonInterface(true)}
                  />
                </div>
              </Suspense>
              <Dial 
                data={cosmicData} 
                onPlanetClick={(planet) => handleElementClick('planet', planet)}
                onElementClick={handleElementClick}
                onCenterClick={() => setShowQuickStatus(true)}
              />
            </div>
            
            {/* Context Tiles - order controlled by CSS media queries */}
            <div className="context-tiles-wrapper flex-shrink-0 w-full sm:w-auto">
              <ContextTiles
                selectedDate={selectedDate}
                extendedDateInfo={extendedDateInfo}
                location={location}
                cosmicData={cosmicData}
                onOpenDatePicker={() => setShowDatePicker(true)}
                onOpenLocationPicker={() => setShowLocationPicker(true)}
                onSetCurrentTime={() => {
                  cosmicCache.clear();
                  setSelectedDate(null);
                  setExtendedDateInfo(null);
                  setCurrentTime(new Date());
                }}
                onSetCurrentLocation={() => {
                  // Trigger geolocation with user feedback
                  // Keep original location data while showing loading state
                  const originalLocation = location;
                  if (location) {
                    setLocation(prev => ({ ...prev, city: 'Locating...' }));
                  }
                  
                  // Helper function to fallback to IP-based geolocation
                  const fallbackToIPGeolocation = async () => {
                    try {
                      toast.info('Using IP-based location...', { duration: 2000 });
                      const response = await fetch('https://ipapi.co/json/');
                      const text = await response.text();
                      const data = JSON.parse(text);
                      
                      if (data.latitude && data.longitude) {
                        cosmicCache.clear();
                        const ipLocation = {
                          latitude: data.latitude,
                          longitude: data.longitude,
                          city: data.city || 'Your Location',
                          state: data.region || '',
                          country: data.country_name || ''
                        };
                        setLocation(ipLocation);
                        localStorage.setItem('tod_global_location', JSON.stringify(ipLocation));
                        toast.success('Location updated', { description: ipLocation.city });
                      } else {
                        throw new Error('No coordinates in IP response');
                      }
                    } catch (err) {
                      logger.error('IP geolocation fallback error:', err);
                      // Restore original location on failure
                      if (originalLocation) {
                        setLocation(originalLocation);
                      }
                      toast.error('Unable to detect location', { 
                        description: 'Please use the Location panel to set your location manually.' 
                      });
                    }
                  };
                  
                  if (!navigator.geolocation) {
                    // Browser doesn't support geolocation, use IP fallback
                    fallbackToIPGeolocation();
                    return;
                  }
                  
                  navigator.geolocation.getCurrentPosition(
                    async (position) => {
                      const { latitude, longitude } = position.coords;
                      let cityName = `${latitude.toFixed(2)}°, ${longitude.toFixed(2)}°`;
                      try {
                        // Clear cache when changing location
                        cosmicCache.clear();
                        // Reverse geocode to get location details
                        const response = await fetch(
                          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`,
                          { headers: { 'User-Agent': 'TheOnescentDial/2.94' } }
                        );
                        const text = await response.text();
                        const data = JSON.parse(text);
                        cityName = data.address?.city || data.address?.town || data.address?.village || data.address?.county || cityName;
                        const newLocation = {
                          city: cityName,
                          state: data.address?.state || data.address?.region || '',
                          country: data.address?.country || '',
                          latitude,
                          longitude
                        };
                        setLocation(newLocation);
                        localStorage.setItem('tod_global_location', JSON.stringify(newLocation));
                        toast.success('Location updated', { description: cityName });
                      } catch (err) {
                        logger.error('Geocoding error:', err);
                        const newLocation = {
                          city: cityName,
                          state: '',
                          country: '',
                          latitude,
                          longitude
                        };
                        setLocation(newLocation);
                        localStorage.setItem('tod_global_location', JSON.stringify(newLocation));
                        toast.success('Location updated', { description: cityName });
                      }
                    },
                    async (error) => {
                      logger.error('Geolocation error:', error);
                      // Use IP-based geolocation as fallback instead of showing error
                      await fallbackToIPGeolocation();
                    },
                    {
                      enableHighAccuracy: true, // Request high accuracy for explicit user action
                      timeout: 10000, // 10 second timeout for user-initiated request
                      maximumAge: 0  // Don't use cached position for explicit request
                    }
                  );
                }}
                onOpenMoonPanel={() => handleElementClick('planet', 'moon')}
                onOpenSunPanel={() => setDeepDivePanel({ isOpen: true, type: 'solarWeather', itemKey: 'general' })}
                onOpenPhasePanel={(phase) => setShowPhasePanel(phase)}
              />
            </div>
          </div>
        ) : apiError || isOffline ? (
          <div 
            className="text-center p-8 max-w-md" 
            data-testid="error-message"
          >
            {/* Offline/Error Icon */}
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--tod-brass)]/10 flex items-center justify-center">
              {isOffline ? (
                <svg className="w-8 h-8 text-[var(--tod-brass)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-[var(--tod-brass)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
            </div>
            
            {/* Error Message */}
            <p className="text-[var(--tod-brass)] text-lg mb-2 font-medium">
              {isOffline ? 'Connection Lost' : 'Celestial Data Unavailable'}
            </p>
            <p className="text-[var(--tod-text-secondary)] text-sm mb-6">
              {apiError || 'Please check your internet connection.'}
            </p>
            
            {/* Retry Button */}
            <button
              onClick={retryFetch}
              disabled={isOffline}
              className="px-6 py-2 bg-[var(--tod-brass)] text-[var(--tod-bg-secondary)] font-bold uppercase tracking-wider text-sm hover:bg-[var(--tod-text)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="retry-btn"
              aria-label="Retry loading celestial data"
            >
              {isOffline ? 'Waiting for Connection...' : 'Try Again'}
            </button>
          </div>
        ) : (
          <div className="text-[var(--tod-brass)] text-base sm:text-xl brass-glow text-center" data-testid="loading-error">
            Error loading data
          </div>
        )}
      </div>

      {/* Date Picker Modal - Lazy loaded */}
      <AnimatePresence>
        {showDatePicker && (
          <Suspense fallback={<LazyFallback />}>
            <DateTimePicker
              onDateSelect={handleDateSelect}
              onLocationChange={handleLocationChange}
              onClose={() => setShowDatePicker(false)}
              currentDate={selectedDate || displayTime}
              cosmicData={cosmicData}
              onOpenAuth={() => setShowAuthModal(true)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Universal Info Panel */}
      <AnimatePresence>
        {universalPanel && (
          <Suspense fallback={<LazyFallback />}>
            <UniversalInfoPanel
              elementType={universalPanel.elementType}
              elementKey={universalPanel.elementKey}
              interpretation={universalPanel.interpretation}
              cosmicData={
                // In comparative mode, use the correct chart's data
                comparativeMode && universalPanel.chartContext 
                  ? (universalPanel.chartContext === 1 ? chart1DisplayData : chart2DisplayData) 
                  : cosmicData
              }
              onClose={() => { 
                setUniversalPanel(null); 
                setPanelChartContext(null); 
                setNavigationSource(null); 
              }}
              onNavigate={handlePanelNavigate}
              onBackToQuickStatus={navigationSource === 'quickStatus' ? () => {
                setUniversalPanel(null);
                setNavigationSource(null);
                setShowQuickStatus(true);
              } : null}
              onOpenDeepDive={(type, itemKey) => setDeepDivePanel({ isOpen: true, type, itemKey })}
              onOpenModulation={(aspectData) => setModulationPanel({ 
                type: 'modulation', 
                data: { 
                  ...aspectData, 
                  // Preserve existing flag_type if set, otherwise default to 'aspect'
                  flag_type: aspectData.flag_type || 'aspect' 
                } 
              })}
              chartLabel={
                comparativeMode && universalPanel.chartContext 
                  ? (universalPanel.chartContext === 1 
                      ? (comparativeMode.chart1?.profile?.name || comparativeMode.relationshipType?.chart1Label || 'Chart 1')
                      : (comparativeMode.chart2?.profile?.name || comparativeMode.relationshipType?.chart2Label || 'Chart 2'))
                  : null
              }
              isRestoredSynthesis={universalPanel.isRestoredSynthesis}
              restoredContext={universalPanel.restoredContext}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Quick Status Overlay - Center Hub Click */}
      <QuickStatusOverlay
        isOpen={showQuickStatus}
        onClose={() => setShowQuickStatus(false)}
        onExpandToFull={() => {
          setShowQuickStatus(false);
          setShowAspectsPanel(true);
        }}
        cosmicData={cosmicData}
        location={location}
        onNavigate={(type, key) => {
          // Track that navigation came from Quick Status
          setNavigationSource('quickStatus');
          handleElementClick(type, key);
        }}
        onOpenAspectPanel={(aspectData) => {
          // Open ModulationDetailPanel for specific aspect
          setShowQuickStatus(false);
          setModulationPanel({
            type: 'aspect',
            data: aspectData.data
          });
        }}
      />

      {/* Modulation Detail Panel (for retrogrades, aspects, stelliums) - Lazy loaded */}
      <AnimatePresence>
        {modulationPanel && (
          <Suspense fallback={<LazyFallback />}>
            <ModulationDetailPanel
              modulationType={modulationPanel.type}
              modulationData={modulationPanel.data}
              cosmicData={
                comparativeMode && panelChartContext 
                  ? (panelChartContext === 1 ? chart1DisplayData : chart2DisplayData) 
                  : cosmicData
              }
              onClose={() => { setModulationPanel(null); setPanelChartContext(null); }}
              onNavigate={handlePanelNavigate}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Ingress Overlay - opened from ticker, AspectsPanel, planet/sign info panels */}
      <AnimatePresence>
        {ingressOverlay && (
          <Suspense fallback={<LazyFallback />}>
            <IngressOverlay
              ingressData={ingressOverlay}
              cosmicData={cosmicData}
              onClose={() => setIngressOverlay(null)}
              onNavigateToTime={(timestamp) => {
                if (!timestamp) return;
                try {
                  const dt = new Date(timestamp);
                  setSelectedDate(dt);
                  cosmicCache.forceRefresh();
                  setIngressOverlay(null);
                  toast.success('Navigating to ingress moment', {
                    description: dt.toLocaleString(undefined, {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    }),
                    duration: 2000
                  });
                } catch (e) {
                  // Defensive fallback - just close the overlay
                  setIngressOverlay(null);
                }
              }}
              onOpenPlanet={(planet) => {
                setIngressOverlay(null);
                handleElementClick('planet', planet);
              }}
              onOpenSign={(sign) => {
                setIngressOverlay(null);
                handleElementClick('zodiac', sign);
              }}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Aspects Panel (OC Panel) */}
      {showAspectsPanel && (
        <div 
          className="fixed inset-0 z-[39]" 
          onClick={() => { setShowAspectsPanel(false); setPanelChartContext(null); setAspectsPanelInitialOverlay(null); }}
          data-testid="aspects-panel-backdrop"
        />
      )}
      <AnimatePresence>
        {showAspectsPanel && (
          <Suspense fallback={
            <div 
              className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-tod-bg-primary z-50"
              style={{ opacity: 0 }}
            />
          }>
            <AspectsPanel
              data={
                // In comparative mode, use the correct chart's data if context is set
                comparativeMode && panelChartContext 
                  ? (panelChartContext === 1 ? chart1DisplayData : chart2DisplayData) 
                  : cosmicData
              }
              isOpen={showAspectsPanel}
              onClose={() => { 
                setShowAspectsPanel(false); 
                setPanelChartContext(null);
                setAspectsPanelInitialOverlay(null); // Clear initial overlay when closing
              }}
              onElementClick={handleElementClick}
              onOpenDeepDive={(type, key) => setDeepDivePanel({ isOpen: true, type, itemKey: key })}
              onDateNavigate={(date) => {
                setSelectedDate(date);
                setShowAspectsPanel(false);
              }}
              onOpenForecast={(type) => {
                setForecastType(type);
                setShowForecastPanel(true);
              }}
              initialOverlay={aspectsPanelInitialOverlay}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Forecast Panel - Weekly/Monthly Cosmic Forecasts */}
      <AnimatePresence>
        {showForecastPanel && (
          <Suspense fallback={<LazyFallback />}>
            <ForecastPanel
              isOpen={showForecastPanel}
              onClose={() => setShowForecastPanel(false)}
              forecastType={forecastType}
              quickNav={cosmicData?.quick_nav}
              referenceDate={selectedDate || null}
              onNavigateToDate={(date) => {
                setSelectedDate(date);
                cosmicCache.forceRefresh();
                setShowForecastPanel(false);
              }}
              onOpenAspectInfo={(aspectData) => {
                // Close forecast first
                setShowForecastPanel(false);
                // Delay to allow dial data to refresh, then open modulation overlay
                setTimeout(() => {
                  // Construct flag object that ModulationOverlay expects
                  const aspectFlag = {
                    flag_type: 'aspect',
                    flag_key: `${aspectData.body1}-${aspectData.body2}-${aspectData.aspect}`,
                    description: `${aspectData.body1.charAt(0).toUpperCase() + aspectData.body1.slice(1)} ${aspectData.symbol || '☌'} ${aspectData.body2.charAt(0).toUpperCase() + aspectData.body2.slice(1)} (${aspectData.aspect})`,
                    planet1: aspectData.body1,
                    planet2: aspectData.body2,
                    aspect_type: aspectData.aspect,
                    nature: aspectData.nature,
                    status: 'applying'
                  };
                  setAspectsPanelInitialOverlay({ 
                    type: 'modulation', 
                    key: aspectFlag.flag_key,
                    label: aspectFlag.description,
                    extraData: { flag: aspectFlag }
                  });
                  setShowAspectsPanel(true);
                }, 1000);
              }}
              onOpenPlanetInfo={(planetName, eventData) => {
                setShowForecastPanel(false);
                // Delay to allow dial data to refresh
                setTimeout(() => {
                  if (eventData?.type === 'station') {
                    // Construct flag object for station (retrograde/direct).
                    // Backend may emit 'station_retrograde'/'station_direct' (Swiss Ephemeris)
                    // OR 'stationing_retrograde'/'stationing_direct' (Skyfield) — handle both.
                    // Use canonical generic flag_type 'station' so ModulationOverlay routes
                    // via description-based dispatch to the correct rich content block.
                    const stationType = eventData.station_type || 'stationing_direct';
                    const isRetro = stationType.includes('retrograde');
                    const stationFlag = {
                      flag_type: 'station',
                      flag_key: `${planetName}-${stationType}`,
                      description: `${planetName.charAt(0).toUpperCase() + planetName.slice(1)} stationing ${isRetro ? 'retrograde' : 'direct'}`,
                      planet: planetName,
                      station_type: stationType
                    };
                    setAspectsPanelInitialOverlay({ 
                      type: 'modulation', 
                      key: stationFlag.flag_key,
                      label: stationFlag.description,
                      extraData: { flag: stationFlag }
                    });
                    setShowAspectsPanel(true);
                  } else if (eventData?.type === 'ingress') {
                    // Open planet info which shows sign placement
                    handleElementClick('planet', planetName);
                  } else {
                    handleElementClick('planet', planetName);
                  }
                }, 1000);
              }}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Location Controller Modal - Lazy loaded */}
      <AnimatePresence>
        {showLocationPicker && (
          <Suspense fallback={<LazyFallback />}>
            <LocationController
              location={location}
              onLocationChange={handleLocationChange}
              onDateSelect={handleDateSelect}
              onClose={() => setShowLocationPicker(false)}
              onOpenAuth={() => setShowAuthModal(true)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Comparative Analysis Setup Modal - Lazy loaded */}
      <AnimatePresence>
        {showComparativeSetup && (
          <Suspense fallback={<LazyFallback />}>
            <ComparativeAnalysisSetup
              isOpen={showComparativeSetup}
              onClose={() => setShowComparativeSetup(false)}
              onActivateComparison={handleActivateComparison}
              currentLocation={location}
              cosmicData={cosmicData}
              onOpenAuth={(mode) => { setShowComparativeSetup(false); setTimeout(() => setShowAuthModal(mode === 'signup' ? true : mode), 100); }}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Comparison Overlay - Full Screen (Mobile-First) */}
      <AnimatePresence>
        {comparativeMode && showComparisonPanel && (
          <Suspense fallback={<LazyFallback />}>
            <ComparisonOverlay
              isOpen={showComparisonPanel}
              onClose={() => {
                // Only hide the panel, don't exit comparison mode
                // Users can click the Compare button in sidebar to exit comparative mode
                setShowComparisonPanel(false);
              }}
              onEdit={() => {
                setShowComparisonPanel(false);
                setShowComparativeSetup(true);
              }}
              onSave={() => saveToHistory('session')}
              onStarSave={() => saveToHistory('starred')}
              isAuthenticated={!!user}
              onOpenAuth={() => setShowAuthModal(true)}
              cosmicData={chart1DisplayData}
              secondaryCosmicData={chart2DisplayData}
              comparativeMode={comparativeMode}
              onElementClick={handleElementClick}
              onOpenDeepSynthesis={() => setShowDeepSynthesis(true)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Comparison Synthesis Panel - Desktop Side Panel (Additional Detail) */}
      {comparativeMode && !showComparisonPanel && (
        <div 
          className="fixed inset-0 z-[39]" 
          onClick={() => setShowComparisonPanel(true)}
          data-testid="comparison-synthesis-backdrop"
        />
      )}
      <AnimatePresence>
        {comparativeMode && !showComparisonPanel && (
          <Suspense fallback={<LazyFallback />}>
            <ComparisonSynthesisPanel
              isOpen={false}
              onToggle={() => setShowComparisonPanel(true)}
              cosmicData={chart1DisplayData}
              secondaryCosmicData={chart2DisplayData}
              comparativeMode={comparativeMode}
              onClose={() => setShowComparisonPanel(false)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Deep Synthesis Reader - Stacks on top of ComparisonOverlay when
          user clicks "Deep Synthesis" affordance. Coexist pattern (R7) so
          the unified CiH voice compare read is reachable without retiring
          the existing ComparisonOverlay. */}
      <AnimatePresence>
        {comparativeMode && showDeepSynthesis && (
          <Suspense fallback={<LazyFallback />}>
            <ComparisonSynthesisPanel
              isOpen={true}
              onToggle={() => setShowDeepSynthesis(false)}
              cosmicData={chart1DisplayData}
              secondaryCosmicData={chart2DisplayData}
              comparativeMode={comparativeMode}
              onClose={() => setShowDeepSynthesis(false)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Auth Modal */}
      <AnimatePresence>
        {showHorizonInterface && (
          <Suspense fallback={<LazyFallback />}>
            <HorizonInterface
              cosmicData={cosmicData}
              onClose={() => setShowHorizonInterface(false)}
              onShowAuth={() => setShowAuthModal(true)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAuthModal && (
          <Suspense fallback={<LazyFallback />}>
            <AuthModal
              isOpen={showAuthModal}
              onClose={() => {
                setShowAuthModal(false);
                setAuthModalMode('login');
                setPasswordResetToken('');
              }}
              initialMode={authModalMode}
              initialResetToken={passwordResetToken}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Complete Profile Modal (for invited users) */}
      <AnimatePresence>
        {showCompleteProfileModal && inviteToken && (
          <Suspense fallback={<LazyFallback />}>
            <CompleteProfileModal
              isOpen={showCompleteProfileModal}
              onClose={(success) => {
                setShowCompleteProfileModal(false);
                setInviteToken(null);
                // If successful, the user is now logged in
              }}
              inviteToken={inviteToken}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* History & Storage Panel */}
      <AnimatePresence>
        {showSearchHistory && (
          <Suspense fallback={<LazyFallback />}>
            <SearchHistory
              isOpen={showSearchHistory}
              onClose={() => setShowSearchHistory(false)}
              initialTab={searchHistoryInitialTab}
              onLoadViewState={loadViewState}
              onOpenSynthesis={handleOpenSynthesis}
              onOpenLocationPanel={() => setShowLocationPicker(true)}
              onOpenProfileManager={() => setShowProfileManager(true)}
              onOpenCalendar={() => setShowDatePicker(true)}
              userProfile={user}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Export/Share Panel */}
      <AnimatePresence>
        {showExportShare && (
          <Suspense fallback={<LazyFallback />}>
            <ExportShare
              isOpen={showExportShare}
              onClose={() => setShowExportShare(false)}
              viewState={{
                date: selectedDate?.toISOString() || null,
                latitude: location?.latitude ?? location?.lat,
                longitude: location?.longitude ?? location?.lng,
                city: location?.city || null,
                state: location?.state || null,
                country: location?.country || null,
                comparison_mode: !!comparativeMode,
                comparison_type: comparativeMode || null
              }}
              cosmicData={cosmicData}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Privacy Policy Modal */}
      <AnimatePresence>
        {showPrivacyPolicy && (
          <Suspense fallback={<LazyFallback />}>
            <PrivacyPolicyModal
              isOpen={showPrivacyPolicy}
              onClose={() => setShowPrivacyPolicy(false)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Change Password Modal */}
      <AnimatePresence>
        {showChangePassword && (
          <Suspense fallback={<LazyFallback />}>
            <ChangePasswordModal
              isOpen={showChangePassword}
              onClose={() => setShowChangePassword(false)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Profile Manager Modal */}
      <AnimatePresence>
        {showProfileManager && (
          <Suspense fallback={<LazyFallback />}>
            <ProfileManager
              isOpen={showProfileManager}
              onClose={() => {
                setShowProfileManager(false);
                setProfileManagerInitialTab('profiles'); // Reset to default
              }}
              initialTab={profileManagerInitialTab}
              onQuickCast={({ datetime, location, profileName }) => {
                // Set the dial to show this profile's orientation
                setSelectedDate(datetime);
                setLocation(location);
                setShowProfileManager(false);
              }}
              onQuickExport={() => {
                // Open export panel and close profile manager
                setShowProfileManager(false);
                setShowExportShare(true);
              }}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Glossary Panel Modal */}
      <AnimatePresence>
        {showGlossaryPanel && (
          <Suspense fallback={<LazyFallback />}>
            <GlossaryPanel
              isOpen={showGlossaryPanel}
              onClose={() => {
                setShowGlossaryPanel(false);
                setGlossaryTargetTerm(null);
                setGlossaryTargetFAQ(null);
                setGlossaryInitialTab('glossary');
              }}
              targetTerm={glossaryTargetTerm}
              targetFAQ={glossaryTargetFAQ}
              initialTab={glossaryInitialTab}
              onTargetTermHandled={() => setGlossaryTargetTerm(null)}
              onOpenDeepDive={(type, key) => {
                setShowGlossaryPanel(false);
                setDeepDivePanel({ isOpen: true, type, itemKey: key });
              }}
              onOpenPhasePanel={(phase) => {
                setShowGlossaryPanel(false);
                setShowPhasePanel(phase);
              }}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Phase Info Panel */}
      <AnimatePresence>
        {showPhasePanel && (
          <Suspense fallback={<LazyFallback />}>
            <PhaseInfoPanel
              phase={showPhasePanel}
              isOpen={!!showPhasePanel}
              onClose={() => setShowPhasePanel(null)}
              onNavigate={handleElementClick}
              interpretations={interpretations}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Keyboard Shortcuts Modal */}
      <AnimatePresence>
        {showKeyboardShortcuts && (
          <Suspense fallback={<LazyFallback />}>
            <KeyboardShortcutsModal
              isOpen={showKeyboardShortcuts}
              onClose={() => setShowKeyboardShortcuts(false)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Admin Panel for Product Keys */}
      <AnimatePresence>
        {showAdminPanel && (
          <Suspense fallback={<LazyFallback />}>
            <AdminPanel
              isOpen={showAdminPanel}
              onClose={() => setShowAdminPanel(false)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Multi-View Visualization (Global View / Temporal View) */}
      <AnimatePresence>
        {multiViewConfig && (
          <Suspense fallback={<LazyFallback />}>
            <MultiViewVisualization
              mode={multiViewConfig.mode}
              primaryLocation={multiViewConfig.primaryLocation}
              locations={multiViewConfig.locations}
              timePoints={multiViewConfig.timePoints}
              datetime={multiViewConfig.datetime}
              onClose={() => setMultiViewConfig(null)}
              onEdit={() => {
                // Close the visualization and reopen setup with existing config
                setMultiViewConfig(null);
                setShowComparativeSetup(true);
              }}
              onSave={() => saveToHistory('session')}
              onStarSave={() => saveToHistory('starred')}
              isAuthenticated={!!user}
              onOpenAuth={() => setShowAuthModal(true)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Time Lapse Player */}
      <AnimatePresence>
        {timeLapseConfig && (
          <Suspense fallback={<LazyFallback />}>
            <TimeLapsePlayer
              config={timeLapseConfig}
              onClose={() => setTimeLapseConfig(null)}
              onEdit={() => {
                // Close the player and reopen setup with existing config
                setTimeLapseConfig(null);
                setShowComparativeSetup(true);
              }}
              onSave={() => saveToHistory('session')}
              onStarSave={() => saveToHistory('starred')}
              isAuthenticated={!!user}
              onOpenAuth={() => setShowAuthModal(true)}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Deep Dive Panel (Element/Modality/Archetype education) */}
      <AnimatePresence>
        {deepDivePanel.isOpen && (
          <Suspense fallback={<LazyFallback />}>
            <DeepDivePanel
              isOpen={deepDivePanel.isOpen}
              onClose={() => setDeepDivePanel({ isOpen: false, type: null, itemKey: null })}
              type={deepDivePanel.type}
              itemKey={deepDivePanel.itemKey}
              onNavigate={handleElementClick}
              cosmicData={cosmicData}
            />
          </Suspense>
        )}
      </AnimatePresence>

      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        <OnboardingTutorial 
          onComplete={() => setShowTutorial(false)}
          forceShow={showTutorial}
        />
      </Suspense>

      {/* Footer - Proprietary Notice with Brand Links */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[var(--tod-bg-secondary)]/90 backdrop-blur-sm border-t border-[var(--tod-brass)]/20 py-2 px-2 sm:px-4 z-30">
        <div className="flex items-center justify-between max-w-screen-xl mx-auto text-[9px] sm:text-[10px]">
          {/* Left side - Cycles in Harmony branding */}
          <div className="flex items-center gap-1 sm:gap-2 text-[var(--tod-text-secondary)]">
            <span className="text-[var(--tod-purple)]">Cycles in Harmony</span>
            <span className="hidden sm:inline opacity-50">•</span>
            <span className="hidden sm:inline">Powered by <span className="text-[var(--tod-brass)]">Onesce Wellbeing</span></span>
          </div>
          
          {/* Right side - responsive copyright and brand links */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            <span className="text-[var(--tod-brass)]/60 font-mono">{getVersionString()}</span>
            <span className="text-[var(--tod-text-secondary)] opacity-50">•</span>
            <button 
              onClick={() => setShowPrivacyPolicy(true)}
              className="text-[var(--tod-text-secondary)] hover:text-[var(--tod-brass)] flex items-center gap-0.5 sm:gap-1 transition-colors p-0.5 sm:p-1"
              data-testid="privacy-policy-link"
            >
              <Shield size={10} className="sm:w-3 sm:h-3" />
              <span className="hidden xs:inline sm:inline">Privacy</span>
            </button>
            <span className="text-[var(--tod-text-secondary)] opacity-50">•</span>
            <a 
              href="https://optimalmastery.life" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[var(--tod-text-secondary)] hover:text-[var(--tod-brass)] transition-colors"
            >
              <span className="sm:hidden">© 2026</span>
              <span className="hidden sm:inline">© 2026 Optimal Mastery LLC</span>
            </a>
            <span className="hidden md:inline text-[var(--tod-text-secondary)] opacity-50">•</span>
            <a 
              href="https://mayryanna.link" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden md:inline text-[var(--tod-text-secondary)] hover:text-[var(--tod-brass)] transition-colors italic"
            >
              mayryanna
            </a>
          </div>
        </div>
      </footer>
      
      {/* CyKL Chat - Floating Dragon Guide */}
      {/* Always visible so it can be highlighted during tutorial */}
      <Suspense fallback={null}>
        <CyKLChat 
          isVisible={true}
          context={cyKLContext}
          showCosmicGreeting={true}
        />
      </Suspense>
      
      {/* Experience Settings - Quick Toggle & Settings Panel */}
      <ExperienceQuickToggle />
      <Suspense fallback={null}>
        <ExperienceSettingsPanel />
      </Suspense>
    </div>
  );
}

export default function AppShell() {
  // Dev-only smoke route for Phase 2 ChartWheel verification. Bypasses the
  // whole app shell (no auth, no dial context) so we can visually verify
  // the wheel in isolation. Remove this shell before deploy if unused.
  if (typeof window !== 'undefined' && window.location.pathname === '/_horizon_smoke') {
    return (
      <Suspense fallback={<div style={{ padding: 20 }}>Loading smoke…</div>}>
        <HorizonSmoke />
      </Suspense>
    );
  }
  // Phase 3 verification route: full Horizon interface without needing a
  // banner entry point yet. Wired to the AuthContext so authenticated users
  // see their chart immediately. Fetches cosmic-positions so the Dial slot
  // renders (letting us verify Phase-3.5 horizon rotation).
  if (typeof window !== 'undefined' && window.location.pathname === '/_horizon') {
    return (
      <Suspense fallback={<div style={{ padding: 20, color: '#C5A059' }}>Loading Horizon…</div>}>
        <HorizonRoute />
      </Suspense>
    );
  }
  return <App />;
}

function HorizonRoute() {
  const [cosmicData, setCosmicData] = useState(null);
  useEffect(() => {
    const api = process.env.REACT_APP_BACKEND_URL;
    fetch(`${api}/api/cosmic-positions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        datetime: new Date().toISOString().slice(0, 19),
        latitude: 39.7392,
        longitude: -104.9848,
      }),
    })
      .then((r) => r.json())
      .then(setCosmicData)
      .catch(() => setCosmicData(null));
  }, []);
  return (
    <HorizonInterface
      cosmicData={cosmicData}
      onClose={() => { window.location.pathname = '/'; }}
      onShowAuth={() => { window.location.pathname = '/'; }}
    />
  );
}
